// Where a person can go, declared once — with what reaching it requires.
//
// # The measurement this exists for
//
// The rail hard-coded eight destinations in four groups and showed all of them to
// everybody. Measured on 2026-08-05 as an ordinary employee with no roles: most of
// those links either refuse on arrival or open an empty screen, because the server
// narrows every read to what that person may see and for them that is themself.
//
// **A link that always refuses is worse than no link.** It teaches somebody that the
// product is broken, and then that the whole rail is decorative.
//
// # The requirement has to be the SAME answer the API gives
//
// This is the part that makes it worth declaring rather than eyeballing. `RouteSpec`
// on the server now carries a `subject:` saying whose record each operation is about,
// and the administering roles are mirrored in `lib/access.ts` with a CI check keeping
// the mirror honest. A destination declares its requirement in those same terms, so
// "can I reach this" is answered here with the vocabulary the server enforces rather
// than with a guess that drifts.
//
// It stays a COURTESY. The server refuses regardless; this only decides what is worth
// offering. A person who types the URL of a screen they cannot use still gets riksu's
// own refusal, which is the honest one.
//
// # One declaration, two consumers
//
// The rail renders it and the command palette lists it. They were two lists before,
// which is how a screen gets added to one and not the other — and the palette is
// exactly where somebody looks for the screen that is missing from the rail.

import type { LucideIcon } from "lucide-react";
import {
  BackpackIcon,
  BookLockIcon,
  BriefcaseBusinessIcon,
  Building2Icon,
  CableIcon,
  CalendarClockIcon,
  CalendarCogIcon,
  CalendarDaysIcon,
  ChartNoAxesCombinedIcon,
  CircleDollarSignIcon,
  DoorOpenIcon,
  FileCheckIcon,
  FileClockIcon,
  FileSignatureIcon,
  FolderArchiveIcon,
  GitBranchIcon,
  GraduationCapIcon,
  HandCoinsIcon,
  IdCardIcon,
  InboxIcon,
  KeyRoundIcon,
  LayoutGridIcon,
  NetworkIcon,
  NewspaperIcon,
  PackageIcon,
  PlaneIcon,
  ReceiptIcon,
  ShieldCheckIcon,
  TargetIcon,
  TimerIcon,
  UserSearchIcon,
  UsersIcon,
} from "lucide-react";
import {
  type Held,
  holdsAny,
  holdsModule,
  holdsScope,
  type ModuleAccess,
  ROLES,
  SCOPES,
} from "../lib/access";
import type { MessageKey } from "../shell/i18n";
import type { NavigationFolderKey } from "./navigation-folders";

/**
 * What reaching a destination takes.
 *
 * `Everyone` is not a weaker version of the others — it is a claim that the screen is
 * useful to somebody holding nothing, which is true of the inbox and of a person's own
 * leave and false of the leavers list.
 *
 * # A SCOPE AND A ROLE ARE TWO QUESTIONS, and `andScope` is not a second list of roles
 *
 * riksu asks both of every route — `Access::Scope(…)` and `require_any_role(…)` — and this
 * vocabulary could only ask one, so it was wrong the moment a destination needed a scope
 * beyond the universal `riksu.read`/`riksu.write`. `/retention` is that destination:
 * measured against the running stack, the rail offered it to a token holding `people.hr`
 * and riksu answered *"this route requires the riksu.audit.read scope"* — **a control
 * offered to somebody the server will refuse**, which is the rule this product keeps
 * everywhere else.
 *
 * They stay SEPARATE MEMBERS rather than one set of strings. A flat list would make
 * `riksu.audit.read` and `people.hr` interchangeable to every reader, which is the collapse
 * `lib/access.ts` is written against — *a scope says what a client was granted; a role says
 * what a person is.*
 *
 * ONE SCOPE AND NOT A LIST, because riksu declares exactly one per route
 * (`Access::Scope(scope::AUDIT_READ)`). A list would invent an any/all question the server
 * does not have; the day a route needs two, that is a change with a reason behind it.
 *
 * It hangs off `role` rather than being a third variant, so the conjunction is expressible
 * without a combinator: adding the next scope-gated destination is one member on the line
 * that is already there. A scope with no role has no call site, and a variant with no call
 * site is what the staleness rules elsewhere refuse.
 *
 * # THE EVIDENCE LOG USES THE ORIGINAL CONJUNCTION
 *
 * `/retention` was the only one, and it should never have been: `5e55fb6` established that
 * every `/v1/retention/*` READ required `riksu.audit.read` while every WRITE required only
 * `riksu.write` — the scope protected LOOKING more strictly than DESTROYING — and moved the
 * reads to `riksu.read`. So the destination's `andScope` was asking for a scope no route
 * needs, and it went with the same change.
 *
 * **The staleness rule above says to delete a variant with no call site, and it is
 * deliberately not applied.** That rule exists to stop a mechanism outliving the NEED for
 * it; here the need is unchanged — riksu declares `Access::Scope` per route, and
 * `/v1/audit-events` still requires `riksu.audit.read`, and its evidence-log destination
 * now uses this conjunction. What went away is one route's wrong scope, not the
 * category.
 *
 * **What keeps it honest is that the tests own it**: `destinations.test.ts` declares a
 * fixture destination and exercises the conjunction through `canReach`, so the mechanism has
 * a live subject rather than a comment promising a future one. If that fixture ever goes,
 * the product evidence-log controls must still pin this member.
 */
export type Requirement =
  | { readonly kind: "everyone" }
  /** Discovery is useful exactly when an original report section can be reached. */
  | { readonly kind: "reports" }
  | {
      readonly kind: "role";
      readonly anyOf: readonly string[];
      /**
       * A scope the token must ALSO carry. Absent means the universal ones suffice.
       *
       * The original evidence-log read requires this independently of its reader roles.
       * Fixture and product controls both pin the conjunction in destinations.test.ts.
       */
      readonly andScope?: string;
    }
  | {
      /**
       * REACHABLE EXACTLY WHEN THIS GROUP IS AN AREA — that is, when the caller can reach
       * MORE THAN ONE other destination in it.
       *
       * # What it is for
       *
       * A landing page for an area. `/administration` is the one that exists: the product
       * owner asked for a screen at that address, and the thing a landing has to answer is
       * *which of these areas are mine* — which is not a role, it is the answer `groupsFor`
       * already computes. Writing it out as a union of the eight administering role lists
       * would be that answer copied, and the copy goes wrong in exactly one direction: a
       * caller holding `compliance.auditor` and NOT `riksu.audit.read` satisfies the union
       * and reaches none of the eight, so they would be offered a landing listing nothing.
       *
       * # WHY "MORE THAN ONE" AND NOT "AT LEAST ONE"
       *
       * Because the landing exists precisely when the area does, and `groupsFor` already
       * draws a one-member group AS that member — `Section`'s rule one level up, that a
       * module with one screen has nothing to navigate between. `finance.admin`
       * administers `/payroll` and nothing else; offering them a landing would turn their
       * one direct icon into a category entry that opens onto a list of one, which is the
       * press spent to hide nothing that `groupsFor` refuses.
       *
       * So `collapsed` and *has a landing* are the same condition by construction rather
       * than by two rules that agree today, which is what `destinations.test.ts` asserts
       * over the whole declaration rather than over the one group that has one.
       *
       * # It reads `DESTINATIONS`, and cannot recurse
       *
       * `permits` skips every `area` requirement when it counts, so a second landing in one
       * group — or a landing in a group containing another — terminates by construction and
       * not because nobody has written one yet.
       */
      readonly kind: "area";
      readonly group: GroupLabel;
    };

/**
 * ONE INNER SCREEN of a module, for the sidebar beside the rail.
 *
 * The icon rail says which module; this says which screen inside it. Adding one is a
 * line in `sections` below and nothing else — `ui/module-nav.tsx` renders whatever the
 * destination in view declares, and no screen writes any of it.
 *
 * `to` names this level's route namespace. A leaf section is a page and therefore owns
 * that path. A section with children is a drill parent: it gives the hierarchy a durable
 * name but is not itself navigable unless a separate leaf is declared for it. Query
 * parameters narrow a page; neither a mailbox nor a navigation level is page state.
 */
interface SectionBase {
  readonly to: string;
  readonly label: MessageKey;
  /** An existing report entrance, discoverable without a second permission declaration. */
  readonly report?: true;
  /**
   * A deeper navigation level owned by this section.
   *
   * This is a drill, not a second row of page tabs. Inbox uses it for Messages:
   * Tasks / Messages / Notices at the first level, then Received / Sent after Messages
   * is entered. The route names every leaf, so refresh and Back restore the same mailbox;
   * the parent path is deliberately not a screen.
   */
  readonly children?: readonly Section[];
  /** A section may belong to a licensed product while its containing utility does not. */
  readonly module?: ModuleAccess;
  /**
   * WHAT REACHING THIS SCREEN TAKES, when it is not what reaching the module takes.
   *
   * Absent means the module's own requirement is the whole answer, which is true of every
   * section that existed before `/recruiting`: `/leave`, `/expense`, `/payroll`, `/assets`
   * and `/retention` each gate the module once and every screen inside it reads the same
   * set. That is why this is optional rather than required — a section repeating its
   * module's gate would be a second copy of one fact, and the two would drift.
   *
   * `/recruiting` is the first module whose screens are gated by TWO server sets, and the
   * split is riksu's rather than a preference. `SEAT_READERS` reads requisitions and
   * postings; `RECRUITERS` reads applications, offers, the headcount and the awaiting-hire
   * report — because an application and an offer select a person's NAME, and being asked to
   * approve a rate is not a reason to be handed the pipeline.
   *
   * Without this member the module could only be gated once and both choices are wrong.
   * Gating on the narrow set hides `/recruiting` from the two roles `SEAT_READERS` exists
   * for, undoing that widening in the browser. Gating on the wide set offers a finance
   * approver four sidebar links that refuse on arrival — which is the
   * link-that-always-refuses defect this whole file was written against, one level down.
   *
   * It stays a COURTESY, like everything else here. The server refuses regardless, and an
   * address somebody was SENT still reaches riksu and is refused by riksu.
   */
  readonly requires?: Requirement;
}

/**
 * Whether this screen owns durable child records beneath its address.
 *
 * Absent keeps section matching exact. Present means a declared detail route stays attached
 * to the list it came from. A child-specific requirement can only exist in that second arm,
 * so TypeScript refuses a widening rule on a section that has no child address to widen.
 *
 * The register and one assigned record are different disclosures. A recruiting interviewer
 * may open the candidate whose current interview names them while the applications list
 * remains a recruiter's surface. The API still checks that concrete assignment; this is the
 * shell courtesy that lets the request reach it instead of turning a valid inbox link into a
 * client-side 404. It does not make the module or its sidebar reachable.
 */
export type Section = SectionBase &
  (
    | {
        readonly ownsChildren?: undefined;
        readonly childRequires?: never;
        readonly childNamespaces?: never;
      }
    | {
        readonly ownsChildren: true;
        readonly childRequires?: Requirement;
        /** Detail routes whose namespace differs from their register's address. */
        readonly childNamespaces?: readonly string[];
      }
  );

/**
 * ONE AREA OF THE PRODUCT, and the rail is built out of these.
 *
 * # The measurement this exists for
 *
 * A group was a bare `MessageKey` written on each destination, and the rail read it as a
 * DIVISION between peers — an `<hr>` in the icon rail, an `<h2>` in the sheet. That works
 * while a group is two or three items and stops working at eight: measured on 2026-08-11,
 * `nav.group.administration` held EIGHT of the rail's fourteen destinations, so more than
 * half of the product's top-level navigation was one area, drawn as eight peers of `/leave`.
 * The product owner reported it as exactly that — *"şu an yönetim tarafının her biri sol
 * railde ayrı tab olmuş"*.
 *
 * A group is therefore a thing rather than a string: it can say that it COLLAPSES into one
 * entry which opens onto its members.
 *
 * # Declaring an `icon` is what makes a group collapse
 *
 * Not a second `collapses` member beside it. The rail is icons, so an entry standing for a
 * whole group needs a glyph and there is nothing else it could take one from — deriving it
 * from the first member would make ADDING a destination change the rail's icon. And a group
 * that is drawn as its members has no use for one. Two members would admit a group that
 * collapses with no glyph, and a state nothing can render is a state better left
 * unrepresentable.
 *
 * # It does NOT list its members
 *
 * That is the property this whole file rests on: adding a destination is one line in
 * `DESTINATIONS` carrying `group:`, and it appears in the rail, the palette and the trail
 * with no other edit. A collapsed group listing its own members here would be that
 * declaration written twice, and the two would drift the first time somebody added a line
 * to one of them.
 */
export interface Group {
  readonly label: MessageKey;
  /**
   * THE GLYPH OF THE ENTRY THIS GROUP COLLAPSES INTO. Absent means it never collapses and
   * is drawn as its members, which is what the other three are.
   */
  readonly icon?: LucideIcon;
}

/**
 * Every group, in rail order.
 *
 * THE ORDER IS THIS LIST'S, and it used to be the order in which each group's first member
 * happened to appear in `DESTINATIONS`. The two agree today, and NOTHING ASSERTS THAT
 * because nothing can: the two orderings are observationally identical for every possible
 * caller, so a test naming one of them would pass on both implementations and would be a
 * test about nothing. What changes is what a later edit costs — moving `/people` above
 * `/leave`, which is a decision about where two destinations sit, would under the old rule
 * have moved the whole `Şirket` block above `Size ait`, silently. A rail whose areas move
 * about as a side effect of reordering an item inside one is a rail people stop scanning.
 *
 * ONLY ADMINISTRATION COLLAPSES. `nav.group.work` has one member, `nav.group.mine` five
 * and `nav.group.company` two — none of them is a wall, and putting any of them behind a
 * press would spend a click to hide four icons. This is the product owner's line to move and
 * moving it is one `icon:` on one line here.
 *
 * THE COUNT FOR `nav.group.mine` READ "three" UNTIL `/assets/mine` LANDED and nothing noticed,
 * and "four" until `/advance` became its own module, which is why it is worth saying that
 * no test asserts it and none should: a count in a comment is a fact about the list below
 * it, and the list is the thing a reader should count. It is corrected here because a stale
 * number teaches that the group is smaller than it is, which is exactly the judgement this
 * paragraph is asking somebody to make.
 */
export const GROUPS = [
  { label: "nav.group.work" },
  { label: "nav.group.mine" },
  { label: "nav.group.company" },
  { label: "nav.group.administration", icon: ShieldCheckIcon },
  // THE UTILITY RAIL'S OWN GROUP. `ALONGSIDE` is drawn by `shell-frame.tsx` and is not
  // grouped there — the rail itself is the grouping, two items deep — but a destination
  // carries a `group` whichever rail draws it, because `trailOf` names it as the level above
  // every address and the palette heads its section with it. Declared here so that the
  // "every group is named, every name is declared" pair below covers both rails rather than
  // being quietly true of one.
  { label: "nav.utility" },
] as const satisfies readonly Group[];

/**
 * WHICH GROUP A DESTINATION MAY NAME, enforced by the compiler.
 *
 * `MessageKey` would admit `nav.group.administraton`, which is a destination that groups
 * with nothing, renders under a heading the catalogue has no string for, and is caught by
 * whoever notices the rail grew an item. A required, narrowed type is refused by `tsc`,
 * which is stronger than any test — and it is why there is no test asserting this half.
 * The other half, a group nobody names, is invisible to a type and IS tested.
 */
export type GroupLabel = (typeof GROUPS)[number]["label"];

/**
 * `GROUPS` with the `icon` READABLE on every entry, and the labels still narrow.
 *
 * `as const` is what buys `GroupLabel`, and it costs this: each entry keeps its own literal
 * shape, so the three that declare no icon have no `icon` property at all and reading one
 * off the union does not type-check. This is the one place that widens the value back to
 * the interface it already satisfies — declared once, next to the thing it is about, rather
 * than as an `in` check at whichever call site happened to need it first.
 */
const DECLARED: readonly (Group & { readonly label: GroupLabel })[] = GROUPS;

export interface Destination {
  /** The module namespace. A module with sections enters through its first reachable leaf. */
  readonly to: string;
  /** A direct default screen when `to` is an area namespace rather than a screen. */
  readonly entry?: string;
  /** Exact application entrances owned by this destination but never drawn as links. */
  readonly entrances?: readonly string[];
  readonly label: MessageKey;
  readonly icon: LucideIcon;
  /** The group it appears under, which must be one `GROUPS` declares. */
  readonly group: GroupLabel;
  readonly requires: Requirement;
  /** The licensed product boundary, or shared infrastructure by explicit declaration. */
  readonly module: ModuleAccess;
  /** The task family shown above this module in a deep administration navigation. */
  readonly navigationFolder?: NavigationFolderKey;
  /**
   * The screens inside this one, if it has more than one.
   *
   * Absent means no sidebar and no toggle. That absence is deliberate rather than an
   * empty column: a module with one screen has nothing to navigate between, and a
   * collapse control for a region that does not exist is a control that does nothing.
   */
  readonly sections?: readonly Section[];
}

/** The actual page opened from a destination entry, never a namespace-only parent. */
export function entryPath(destination: Destination): string {
  let section = destination.sections?.[0];
  while (section?.children?.[0]) section = section.children[0];
  return section?.to ?? destination.entry ?? destination.to;
}

const EVERYONE: Requirement = { kind: "everyone" };

/**
 * THE CANDIDATE HALF OF RECRUITING, named once because three sections carry it.
 *
 * Written out as a `const` rather than three object literals for the reason `sources.ts`
 * gives about its factories: three copies of one correct declaration behave exactly like
 * one, right up to the moment somebody widens the set in the screen they happen to be
 * reading — and then the pipeline is offered to a role the server refuses on one of the
 * three, with nothing anywhere going red.
 */
const RECRUITING_PIPELINE: Requirement = { kind: "role", anyOf: ROLES.recruiting };
/**
 * THE AGENCY REGISTER — this module's THIRD server set, and the only one that reaches
 * outside recruiting altogether.
 *
 * `recruiting/agencies.rs` gates the register on `AGENCY_READERS` and the agreement writes
 * and the coverage report on `AGREEMENT_KEEPERS`, neither of which is `RECRUITERS` or
 * `SEAT_READERS`: registering a counterparty is a commercial act, and the recruiter who
 * receives the CVs recording the agreement that covers them is the counterparty marking its
 * own homework.
 */
const RECRUITING_AGENCIES: Requirement = { kind: "role", anyOf: ROLES.agencyReaders };
/**
 * WHAT REACHING THE AREA TAKES, which is now the UNION of two sets rather than one.
 *
 * The module gate was `recruitingSeats` alone, and it has to widen for the same reason
 * `SEAT_READERS` was added on the server: `compliance.auditor` holds NO recruiting endpoint
 * except this register, so gating the area on the seat half would leave the person the
 * register was built for with no rail item, no palette entry and no way in — the exact gap
 * `recruitingSeats` closed, arriving one section along.
 *
 * `narrowedFor` then does the rest and this is the first destination it does it for
 * non-trivially: a caller holding `compliance.auditor` alone is offered ONE section, because
 * every other one carries `RECRUITING_PIPELINE` or the module's own gate. The alternative
 * was a second top-level destination for a register whose only join is an application, which
 * would put a compliance surface in the rail beside `/leave`.
 */
const RECRUITING_AREA: Requirement = {
  kind: "role",
  anyOf: [
    ...new Set([
      ...ROLES.recruitingSeats,
      ...ROLES.agencyReaders,
      ...ROLES.requisitionReportReaders,
    ]),
  ],
};

/**
 * Every screen this product serves, in rail order.
 *
 * Adding one is a line here and nothing else: the rail, the palette and the
 * reachability test all read this. A screen that exists without a line fails
 * `every_route_has_a_destination` rather than being quietly unreachable.
 */
/**
 * WHAT ACCOMPANIES THE WORK, on the right rather than among the modules.
 *
 * The inbox and the notifications are not places you go to do a job; they are what you
 * glance at while doing one. Keeping them in the module rail put them in the same list as
 * leave and expense — and when the utility rail arrived they appeared in BOTH, which is
 * two answers to "where is my inbox".
 *
 * They are declared here rather than in `DESTINATIONS` so the two rails cannot overlap by
 * construction: a destination is in one list or the other.
 */
export const ALONGSIDE: readonly Destination[] = [
  {
    to: "/inbox",
    label: "nav.inbox",
    icon: InboxIcon,
    group: "nav.utility",
    module: { kind: "foundation" },
    // Not role-gated. An inbox holds what is routed to the caller or to a role they
    // hold, so it is EMPTY rather than forbidden for somebody with nothing waiting --
    // and hiding it would hide the empty state, which is the answer to "is there
    // anything for me".
    requires: EVERYONE,
    sections: [
      { to: "/inbox/tasks", label: "nav.inbox.tasks", ownsChildren: true },
      {
        to: "/inbox/service-requests",
        label: "serviceRequests.heading",
        ownsChildren: true,
        module: { kind: "licensed", module: "people" },
      },
      {
        to: "/inbox/tasks/onboarding",
        label: "nav.onboarding.tasks",
        module: { kind: "licensed", module: "onboarding" },
        children: [
          {
            to: "/inbox/tasks/onboarding/support",
            label: "nav.onboarding.support",
            ownsChildren: true,
          },
          {
            to: "/inbox/tasks/onboarding/reviews",
            label: "development.heading",
            ownsChildren: true,
            module: { kind: "licensed", module: "performance" },
          },
          {
            to: "/inbox/tasks/onboarding/final-reviews",
            label: "finalReview.heading",
            ownsChildren: true,
            module: { kind: "licensed", module: "performance" },
          },
        ],
      },
      {
        to: "/inbox/messages",
        label: "nav.inbox.messages",
        module: { kind: "licensed", module: "messaging" },
        children: [
          {
            to: "/inbox/messages/received",
            label: "nav.inbox.messages.received",
            ownsChildren: true,
          },
          {
            to: "/inbox/messages/sent",
            label: "nav.inbox.messages.sent",
            ownsChildren: true,
          },
        ],
      },
      {
        to: "/inbox/tasks/offboarding",
        label: "offboarding.assigned.heading",
        module: { kind: "licensed", module: "offboarding" },
      },
      { to: "/inbox/notices", label: "nav.inbox.notices", ownsChildren: true },
    ],
  },
  {
    // THE LOGIN, WHICH IS NOT THE PERSON — and they had one screen between them.
    //
    // The product owner: *"look at what you've crammed into the session/profile screen — is
    // that normal? The things that are personal to the user each have their own area."* `/`
    // held a person's employment, seat, leave balance, custody, advances, employment
    // history and özlük dosyası — which are one subject, correctly composed, and the same
    // seven blocks a person's record pane draws about anybody — and then, in the same grid,
    // a principal identifier, a tenant handle, a scope list and a token expiry.
    //
    // Those last four are not facts about the reader. They are facts about the CREDENTIAL
    // the reader signed in with, and the tell is that they survive the reader having no
    // employment record at all: a support engineer or a partner administrator has every one
    // of them and none of the seven above. A screen holding both answers *who am I* and
    // *what am I signed in as* in one column, and only one of those questions is ever asked
    // by somebody looking at their leave balance.
    //
    // IN THE UTILITY RAIL RATHER THAN AMONG THE MODULES, which is that rail's own stated
    // rule: these are not places you go to do a job, they are what you glance at while
    // doing one. The account is the sharpest instance of it in the product — it is opened
    // once, to read a string out to somebody on a support call.
    //
    // `everyone`, and that is a claim rather than a default: it is the one screen in riksu
    // that is useful to a login riksu cannot resolve to a person, because it is the screen
    // that SAYS SO.
    to: "/account",
    label: "nav.account",
    icon: KeyRoundIcon,
    group: "nav.utility",
    module: { kind: "foundation" },
    requires: EVERYONE,
    sections: [
      { to: "/account", label: "nav.account.overview" },
      { to: "/account/delegations", label: "nav.account.delegations" },
    ],
  },
];

export const DESTINATIONS: readonly Destination[] = [
  {
    to: "/system/audit-logs",
    module: { kind: "foundation" },
    label: "auditTrail.heading",
    icon: FileClockIcon,
    group: "nav.group.administration",
    navigationFolder: "records-compliance",
    requires: { kind: "role", anyOf: ROLES.auditReaders, andScope: SCOPES.auditRead },
  },
  {
    to: "/reports/catalog",
    module: { kind: "foundation" },
    label: "reportCatalogue.heading",
    icon: ChartNoAxesCombinedIcon,
    group: "nav.group.company",
    requires: { kind: "reports" },
  },
  // First in the product, deliberately. Everything else is somebody STARTING
  // something; this is the only place anything gets finished.
  // BESIDE the inbox, because they are a pair and the point is that they are two
  // lists. "What needs me" blocks; "you should know" does not.
  // The personal work landing and existing profile share one foundation destination.
  {
    to: "/me",
    entry: "/me/work",
    entrances: ["/"],
    label: "workspace.heading",
    icon: IdCardIcon,
    group: "nav.group.work",
    module: { kind: "foundation" },
    requires: EVERYONE,
  },
  {
    to: "/calendar",
    label: "nav.calendar",
    icon: CalendarClockIcon,
    group: "nav.group.work",
    module: { kind: "foundation" },
    requires: EVERYONE,
    sections: [
      { to: "/calendar", label: "nav.calendar" },
      {
        to: "/calendar/team-leave",
        label: "teamCalendar.nav",
        module: { kind: "licensed", module: "leave" },
      },
    ],
  },
  // A person's own. `riksu-authz` narrows these to themself, so they are useful to
  // somebody holding nothing — which is the whole reason `everyone` is a real answer
  // rather than a default.
  {
    to: "/leave",
    module: { kind: "licensed", module: "leave" },
    label: "nav.leave",
    icon: CalendarDaysIcon,
    group: "nav.group.mine",
    requires: EVERYONE,
    // WHAT WERE TABS. A strip under the page title works for two and stops working at
    // six, and it had nowhere to grow; the sidebar grows down, which is the axis a list
    // of screens wants.
    //
    // THE CALENDAR SETUP IS NO LONGER ONE OF THESE. It is a destination in
    // `nav.group.administration` — see `/leave/setup` below, which carries the reasoning.
    // What is left here is three screens with one audience: the reader's own leave.
    // WHAT I SENT AND WHAT IS WAITING ON ME ARE TWO SCREENS, and they were one control on
    // the balances screen. `requests.tsx` put them behind a segmented control writing
    // `?showing=awaiting`, and its own header argues — correctly — that the choice belongs
    // in the ADDRESS because it changes which answer you are looking at rather than how you
    // are looking at one. What it then chose was a query parameter, and this file's oldest
    // rule settles the rest: *a section is a PLACE — a parameter narrows a place, it does
    // not get to be one.* Two answers with no row in common are two places.
    //
    // The product owner asked for it in exactly those terms about the module next door:
    // *"don't try to show more than one thing in one place — do it properly"*.
    //
    // NEITHER TAKES A `requires`, which is the member doing its job rather than being
    // forgotten. Approving leave is not a role in riksu — it is being the manager the
    // process routed to — so the awaiting list is EMPTY rather than forbidden for somebody
    // with nothing waiting, which is the inbox's own argument for not being gated either.
    // Hiding it would hide the empty state, and the empty state is the answer to *is there
    // anything for me*.
    sections: [
      { to: "/leave/balances", label: "nav.leave.balances" },
      { to: "/leave/history", label: "nav.leave.history", ownsChildren: true },
      { to: "/leave/requests", label: "nav.leave.requests", ownsChildren: true },
      { to: "/leave/approvals", label: "nav.leave.approvals", ownsChildren: true },
      // MD. 56'S CORRECTION QUEUE, and it takes no `requires` for the two reasons the
      // approvals line above takes none. `GET /v1/leave/sickness-overlaps` narrows to the
      // person THEMSELF — it is one of the four `SpecialCategoryList` reads — so an
      // ordinary employee sees their own corrections without holding anything, which is
      // what makes `everyone` a real answer here rather than a default. And gating it on
      // `ROLES.leave` would hide the EMPTY state, which is the answer to *am I owed days
      // back for the holiday I spent ill*: a worker is the first person to ask that, and
      // riksu answers it for them and for nobody else unless they administer leave.
      { to: "/leave/corrections", label: "nav.leave.corrections" },
    ],
  },
  {
    to: "/expense",
    module: { kind: "licensed", module: "expense" },
    label: "nav.expense",
    icon: ReceiptIcon,
    group: "nav.group.mine",
    requires: EVERYONE,
    // AND EXPENSE APPROVALS IS ITS OWN SCREEN, which is the product owner's own words:
    // *"Expense Approvals, the screen you made, should be a separate sidebar element."*
    //
    // It was a `Section` at the foot of the claims screen carrying a segmented control with
    // two lists behind it, so the module's landing screen held three subjects — an advance
    // balance, this person's claims, and other people's money waiting on this person's
    // decision. The last of those is the one with a different audience and a different act,
    // and it was the one with no address.
    //
    // THE PAIR IS THE SAME PAIR `/leave` TAKES, one module along, and for the same reason —
    // see the note there. It spans this module's ONE chain, `expense.claim`, and the advance
    // chain is `/advance`' own pair below: the two used to share a `requests` screen on the
    // argument that *what did I send* is one question about money owed in either direction,
    // and that argument is what kept the avans inside the claim.
    sections: [
      { to: "/expense/claims", label: "nav.expense.claims", ownsChildren: true },
      {
        to: "/expense/payments",
        label: "nav.expense.payments",
        requires: { kind: "role", anyOf: ROLES.paymentReaders },
      },
      {
        to: "/expense/reports",
        label: "nav.expense.reports",
        report: true,
        requires: { kind: "role", anyOf: ROLES.expenseReportReaders },
      },
      { to: "/expense/requests", label: "nav.expense.requests", ownsChildren: true },
      { to: "/expense/approvals", label: "nav.expense.approvals", ownsChildren: true },
    ],
  },
  {
    // THE EMPLOYEE AND MANAGER WORK SURFACE. A person's own pay, issued statements and
    // manager proposals belong to the working-life area; tenant-wide pay records, plans,
    // bands and payroll inputs are company configuration and live under the separate
    // `/compensation/management` administration destination below.
    to: "/compensation",
    module: { kind: "licensed", module: "compensation" },
    label: "nav.compensation",
    icon: CircleDollarSignIcon,
    group: "nav.group.mine",
    requires: EVERYONE,
    sections: [
      { to: "/compensation/mine", label: "nav.compensation.mine", ownsChildren: true },
      {
        to: "/compensation/awards",
        label: "nav.compensation.awards",
        ownsChildren: true,
      },
      {
        to: "/compensation/bonuses",
        label: "nav.compensation.bonuses",
        ownsChildren: true,
      },
      {
        to: "/compensation/merit",
        label: "nav.compensation.merit",
        ownsChildren: true,
      },
    ],
  },
  {
    // THE AVANS, AS ITS OWN MODULE — asked for three times, and the third time the answer
    // came back that it already was. It was not: it was a `Section` of `/expense`, which is
    // *"two sections under one module"* and not *"two modules"*.
    //
    // AN ADVANCE IS NOT AN EXPENSE. A claim is money already spent being reclaimed; an
    // advance is company money **already in a worker's hands** before anything has been
    // bought. Two approval gates where a claim has one, a `cash_advance` row written by the
    // EFFECT when finance approves rather than by the request, and an audience — finance —
    // that a person's own receipts do not have. They are two objects with two lifecycles
    // that meet at exactly ONE point, the settlement, and `least(still_held, claim_total)`
    // is the whole of that meeting. Where a product has the concept at all it gives the
    // advance its own place in the navigation and joins it to claims only there.
    //
    // AND IT IS WHAT DISSOLVES THE NAMING PROBLEM rather than a rename. `nav.expense` is
    // *Masraflar* and `nav.expense.claims` is *Masraf talepleri* — parent and child nearly
    // the same word — which is why *Avanslar* underneath read as a KIND of claim. Two
    // top-level destinations remove the false parent, so nothing here is renamed.
    //
    // `/advance` AND NOT `/expense/advances`. `moduleFor` is longest-prefix, so the old
    // address would have carried its own group, crumb and gate perfectly well — the
    // `/leave/setup` precedent — and that is an argument about the MECHANISM rather than
    // about this. What it cannot do is stop the address saying the thing the split exists to
    // deny: a reader who copies `/expense/advances` into a message has just filed the avans
    // under expenses again. Every other module in this group is one segment.
    //
    // `everyone`, like `/expense`: asking for an advance is self-service — `POST
    // /v1/advances/requests` opens two gates and hands over nothing — and
    // `riksu-authz` narrows the ledger to the caller and their subtree. The one write that
    // takes `finance.admin` is recording a HAND-BACK, and the screen hides that row action
    // rather than the module.
    to: "/advance",
    module: { kind: "licensed", module: "advance" },
    label: "nav.advances",
    icon: HandCoinsIcon,
    group: "nav.group.mine",
    requires: EVERYONE,
    // THE SAME THREE-SCREEN SHAPE `/leave` and `/expense` take, and the pair is not
    // decoration: the advance chain has TWO gates, so an advance waiting on somebody's
    // decision is a real list — and while the two modules shared `/expense/approvals` that
    // list appeared under a heading naming the other module's money.
    sections: [
      { to: "/advance/balances", label: "nav.advances.balances", ownsChildren: true },
      { to: "/advance/requests", label: "nav.advances.requests", ownsChildren: true },
      { to: "/advance/approvals", label: "nav.advances.approvals", ownsChildren: true },
    ],
  },

  {
    // A business trip is approved before it is bought. The request, risk review and
    // itinerary are one travel lifecycle; receipts created afterwards remain expenses.
    to: "/travel",
    module: { kind: "licensed", module: "travel" },
    label: "nav.travel",
    icon: PlaneIcon,
    group: "nav.group.mine",
    requires: EVERYONE,
    sections: [
      { to: "/travel/requests", label: "nav.travel.requests", ownsChildren: true },
      { to: "/travel/sent", label: "nav.travel.sent", ownsChildren: true },
      { to: "/travel/approvals", label: "nav.travel.approvals", ownsChildren: true },
    ],
  },

  {
    // TIME AND ATTENDANCE. `riksu-time` has held İş Kanunu md. 41, 63, 68 and 69 since
    // 2026-08-02 and the four routes over it landed after; nothing in the product reached
    // any of them, so "did this person work a night shift" was answered in a spreadsheet.
    //
    // `everyone`, and that is a claim rather than a default. All four routes guard through
    // `Requirement::about_a_person_administered_by(TIME_ADMINISTRATORS)`, whose first two
    // relations are `Themself` and `Manager` — attendance narrows to the ordinary subtree
    // rather than to `themself_only`, deliberately, because a line manager approves rosters
    // and a payslip is the thing they have no business seeing. A role gate here would take
    // the screen from the two audiences it is mostly for and leave it to payroll.
    //
    // In `nav.group.mine` beside leave and expense: what riksu records about the reader's
    // own working life. The administering caller reads it here too, one person at a time,
    // because that is the shape of the endpoint — every one of the four names an
    // `employment_id` and there is no tenant-wide roll-up to put anywhere else.
    to: "/time",
    module: { kind: "licensed", module: "time" },
    label: "nav.time",
    icon: TimerIcon,
    group: "nav.group.mine",
    requires: EVERYONE,
    sections: [
      { to: "/time/entries", label: "time.entries" },
      {
        to: "/time/assessment",
        label: "time.assessment",
        children: [
          { to: "/time/assessment/summary", label: "time.assessment" },
          { to: "/time/assessment/shifts", label: "time.shifts" },
          { to: "/time/assessment/days", label: "time.days" },
          { to: "/time/assessment/holidays", label: "time.holidays" },
        ],
      },
      { to: "/time/overtime", label: "time.bands" },
      { to: "/time/annual-ceiling", label: "time.ceiling" },
      // BOTH REGISTERS SIT HERE RATHER THAN UNDER ADMINISTRATION, and each for its own
      // reason. A serbest zaman election is the worker's own act — md. 41 gives it to
      // nobody else — so the register belongs beside their timesheet. The md. 43
      // authorisation is read by everybody because the route takes no role: it is the
      // rule everybody at the workplace is measured against, and the write that records
      // one is gated on the server.
      { to: "/time/overtime-elections", label: "time.elections" },
      { to: "/time/extraordinary-work", label: "time.authorisations" },
    ],
  },

  {
    // A namespace with durable task surfaces. A work queue, a goal register, a review
    // assignment and an activity summary have different owners and different refresh
    // rates; mounting them together made entering one job fetch every other job too.
    to: "/performance",
    module: { kind: "licensed", module: "performance" },
    label: "nav.performance",
    icon: TargetIcon,
    group: "nav.group.mine",
    requires: EVERYONE,
    sections: [
      { to: "/performance/work", label: "performance.continuous.workItems" },
      { to: "/performance/goals", label: "performance.goals.heading", ownsChildren: true },
      {
        to: "/performance/review-work",
        label: "performance.reviews.heading",
        children: [
          {
            to: "/performance/contributions",
            label: "performance.contribution.tasks",
            ownsChildren: true,
          },
          {
            to: "/performance/reviews",
            label: "performance.overview.assignments",
            ownsChildren: true,
          },
          { to: "/performance/packets", label: "performance.overview.packets", ownsChildren: true },
          {
            to: "/performance/calibration",
            label: "calibrationSession.heading",
            ownsChildren: true,
            requires: { kind: "role", anyOf: ROLES.calibrationPreparers },
          },
          {
            to: "/performance/cycles",
            label: "performance.overview.cycles",
            ownsChildren: true,
            requires: { kind: "role", anyOf: ROLES.performance },
          },
          { to: "/performance/reports", label: "performanceReport.heading", report: true },
        ],
      },
      {
        to: "/performance/continuous",
        label: "performance.areas.heading",
        children: [
          {
            to: "/performance/check-ins",
            label: "performance.checkIns.heading",
            ownsChildren: true,
          },
          {
            to: "/performance/one-to-ones",
            label: "performance.oneToOnes.heading",
            ownsChildren: true,
          },
          {
            to: "/performance/achievements",
            label: "performance.achievements.heading",
            ownsChildren: true,
          },
          {
            to: "/performance/feedback/received",
            label: "performance.feedback.received",
            ownsChildren: true,
          },
          {
            to: "/performance/feedback/requested",
            label: "performance.feedback.requested",
            ownsChildren: true,
          },
        ],
      },
      {
        to: "/performance/activity",
        label: "performance.continuous.adminHeading",
        requires: { kind: "role", anyOf: ROLES.performance },
      },
    ],
  },

  {
    to: "/trainings",
    module: { kind: "licensed", module: "learning" },
    label: "learning.heading",
    group: "nav.group.mine",
    icon: GraduationCapIcon,
    requires: EVERYONE,
    sections: [
      { to: "/trainings", label: "learning.catalogue", ownsChildren: true },
      { to: "/trainings/applications", label: "learningOwn.title", ownsChildren: true },
      {
        to: "/trainings/admin",
        label: "learning.manage",
        requires: { kind: "role", anyOf: ROLES.learning },
      },
      {
        to: "/trainings/reports",
        label: "learningActivity.heading",
        report: true,
        requires: { kind: "role", anyOf: ROLES.learning },
      },
    ],
  },
  {
    // One module, seven durable work surfaces. The catalogue remains everybody's; private
    // matching, expressions, ownership and assignment administration are separate reads
    // and addresses rather than eight sections mounted under one heading.
    to: "/mobility",
    module: { kind: "licensed", module: "mobility" },
    label: "nav.mobility",
    icon: BriefcaseBusinessIcon,
    group: "nav.group.mine",
    requires: EVERYONE,
    sections: [
      {
        to: "/mobility/opportunities",
        label: "mobility.catalogue.heading",
        ownsChildren: true,
      },
      { to: "/mobility/interests", label: "mobility.expression.mineHeading" },
      { to: "/mobility/preferences", label: "mobility.preference.heading" },
      { to: "/mobility/owned", label: "mobility.owned.heading", ownsChildren: true },
      {
        to: "/mobility/assignments",
        label: "nav.mobility.assignments",
        children: [
          { to: "/mobility/assignments/mine", label: "mobility.assignment.mineHeading" },
          {
            to: "/mobility/assignments/managed",
            label: "mobility.assignment.ownedHeading",
          },
          {
            to: "/mobility/assignments/endorsements",
            label: "mobility.assignment.endorsementsHeading",
          },
        ],
      },
    ],
  },

  {
    // A worker can inspect exactly what bounded posture was recorded for devices in
    // their own custody, plus the collection purpose and the categories never collected.
    to: "/device-security/mine",
    module: { kind: "licensed", module: "device_security" },
    label: "nav.deviceSecurityMine",
    icon: ShieldCheckIcon,
    group: "nav.group.mine",
    requires: EVERYONE,
  },

  {
    // A PERSON'S OWN ZİMMET, and the destination `/assets` says in its own comment is not
    // needed. That comment is below and it is TRUE — a person's custody is on their profile
    // — which is why this is a PLACE rather than a disclosure: `/` states the reader's leave
    // balance and `/leave` is the surface, `/` states their outstanding advances and
    // `/expense` is the surface, and custody was the one with a block and nowhere to stand.
    //
    // Reported as *"there is a manager screen but no user screen"*, which is right about the
    // place and wrong about the sight of it.
    //
    // IT IS `/assets/mine` SINCE 2026-08-17, and the paragraph that said it could not be is
    // kept here because the reason was mechanical and the mechanism does not say what it was
    // read as saying.
    //
    // It read: *"NOT `/assets/mine`… `moduleFor` resolves by LONGEST PREFIX, so an address
    // under `/assets` would belong to the administration destination below — inheriting its
    // `ROLES.assets` gate in the rail, its crumb and its sidebar. A screen for everybody
    // cannot live inside the module gated against them."*
    //
    // Every clause is true OF AN ADDRESS THAT IS NOT ITSELF A DESTINATION, and that is the
    // case it silently assumed. `moduleFor` takes the longest `to` that OWNS the path, so a
    // destination declared at `/assets/mine` (twelve characters) beats `/assets` (seven) for
    // its own address and for everything under it. `/leave/setup` is the same mechanism in
    // this file, pointing the other way: a NARROWER screen sitting under a WIDER module's
    // path, and its own comment says so — *"a destination in its own right and `/leave` is a
    // prefix of it, and the longer one wins"*. The rule is symmetric; only the example was
    // one-sided.
    //
    // WHY IT MOVED. The product owner asked twice why looking at your own equipment took a
    // different address from administering it — *"kişi kendi assetslerine bakarken de aynı
    // url'den gitmesi gerekmez mi, ayrı bir route neden var custody diye"* — and the answer
    // was a mechanism that does not bind. Two URL trees for one thing is two vocabularies a
    // reader has to learn, and the word `custody` had stopped naming an address anywhere
    // else: the contract renamed its own to `/v1/assets/holdings` when the module was split.
    //
    // The ADDRESS names the object — assets — while the legal relation inside it remains a
    // zimmet. "Zimmetle", "zimmet tutanağı" and the signed holding record do not move; a
    // navigation label must say what the person will find, not which one relation the module
    // can record.
    //
    // `everyone`, and that is a claim rather than a default — the same one `/leave` and
    // `/expense` make. Measured against the running stack before this line was written: a
    // token minted with `roles=` reads `GET /v1/assets/holdings` and gets its own row, and
    // `GET /v1/assets/damage-claims` narrows through `themself_only`. Neither refuses, so
    // this is not a link that refuses on arrival.
    to: "/assets/mine",
    module: { kind: "licensed", module: "assets" },
    label: "nav.equipment",
    icon: BackpackIcon,
    group: "nav.group.mine",
    requires: EVERYONE,
    // TWO SCREENS NOW, and the second half of the module only existed from 2026-08-13.
    //
    // This carried "ONE SCREEN AND THEREFORE NO `sections`" and the reason was sound for
    // what was there: what you hold and what is claimed against you are one question about
    // one relationship, and a sidebar between them would make the second a place somebody
    // has to know to go. **Those two are still one screen and that argument is untouched.**
    //
    // What changed is that the module gained a VERB. `POST /v1/assets/requests` landed with
    // migration 0108 — the one self-service write in a module that had none — and asking
    // for equipment is not a fact about the relationship, it is a thing somebody does. The
    // product owner asked for exactly this split: *"requests and what-you-hold are normally
    // two separate screens, aren't they?"*
    //
    // THE ORDER IS WHAT A READER OPENS THE MODULE FOR. What they hold is the standing
    // answer and the landing screen; asking is the deliberate act, and it is second for
    // `/expense`'s reason — the everyday reading first, the thing you came to start second.
    //
    // NEITHER TAKES A `requires`, which is the member doing its job rather than being
    // forgotten: both are `everyone` because riksu narrows the custody list to the caller
    // and records a request for the caller with no `person_id` on the body at all.
    //
    // AND THERE IS NO THIRD, which is the decision worth reading before adding one.
    // `/leave` and `/expense` both carry an approvals screen and this deliberately does
    // not: the first `ASSET_REQUEST` gate resolves the requester's line manager and the
    // second routes to `assets.manager`, but both decisions live in the generic inbox.
    // Putting an approver queue under this possessive destination would make *Your
    // equipment* contain other people's requests. The argument is written out in
    // `asset-request.tsx`.
    // AND THE THIRD IS THE CLAIMS TABLE, which was a `Section` on the FIRST screen — so the
    // rail entry read *What you hold* over a page carrying what is claimed against you as
    // well. One destination, one thing, applied to the one module that had quietly kept two.
    //
    // THE ORDER IS THE STANDING ANSWER, THE ACT, THEN THE EXCEPTION. `custody.tsx`'s own
    // header argues that the CLAIMS table is what earns the module and the holding list is
    // context — which invites putting claims first and is answered there rather than here:
    // *earning the surface* is why it exists, and this list is normally EMPTY, so leading
    // with it puts the exceptional case in front of the ordinary one.
    sections: [
      { to: "/assets/mine/holdings", label: "nav.equipment.holding" },
      { to: "/assets/mine/requests", label: "nav.equipment.requests" },
      { to: "/assets/mine/claims", label: "nav.equipment.claims" },
    ],
  },

  {
    // THE READER'S OWN FILE, distinct from `/documents`, which is the tenant-wide chase
    // list for administrators. The server narrows this read to the principal; the route
    // carries no role and the longer destination wins over `/documents` for navigation,
    // breadcrumbs and command search.
    //
    // LAST AMONG THE PERSONAL WORKSPACES because it is a reference file rather than a daily
    // task. Leave, expense, time and current assets are the places a worker acts; this is
    // where they occasionally verify what has been filed. The declaration order is the
    // navigation order, so the frequency judgement stays visible here rather than in a
    // renderer-side list of path exceptions.
    to: "/documents/mine",
    module: { kind: "foundation" },
    label: "nav.documentsMine",
    icon: FolderArchiveIcon,
    group: "nav.group.mine",
    requires: EVERYONE,
  },

  // One place for people: current colleagues and authorized personnel records.
  // The two projections retain their own server authority. The old directory URL
  // is an entrance that redirects here, never a second rail or palette entry.
  {
    to: "/people",
    entrances: ["/directory"],
    module: { kind: "licensed", module: "people" },
    label: "nav.people",
    icon: UsersIcon,
    group: "nav.group.company",
    requires: EVERYONE,
  },
  {
    to: "/announcements",
    module: { kind: "licensed", module: "messaging" },
    label: "news.heading",
    icon: NewspaperIcon,
    group: "nav.group.company",
    requires: EVERYONE,
  },
  {
    to: "/company-policies",
    module: { kind: "licensed", module: "people" },
    label: "companyPolicies.heading",
    icon: FolderArchiveIcon,
    group: "nav.group.company",
    requires: EVERYONE,
  },
  {
    to: "/organisation",
    module: { kind: "licensed", module: "organization" },
    label: "nav.organisation",
    icon: NetworkIcon,
    group: "nav.group.company",
    requires: EVERYONE,
    // ONE SCREEN, AND THEREFORE NO `sections`. The register of workplaces lived here for
    // part of 2026-08-07 and the module carried a two-item sidebar; the product owner
    // settled it the other way — *"olması gereken yerde bir tane olsun yeter"* — and
    // registering a workplace is company setup, so it is a `/setup` step and this module
    // is the chart alone.
    //
    // The one-item `sections` it left behind is the thing this comment exists to stop
    // coming back: the docstring on `Section` argues against it by name — a module with
    // one screen has nothing to navigate between, and a collapse control for a region
    // that does not exist is a control that does nothing. It rendered a sidebar of one
    // link plus its toggle for an hour.
    //
    // It stays `everyone`: every read here is `Subject::Impersonal` and takes
    // `riksu.read` alone, because the shape of the company is a fact about the company
    // rather than about any person. The WRITE on it — put somebody in this seat — is
    // gated on the screen by the role riksu requires for it.
  },

  {
    // THE AREA'S OWN SCREEN, and it overturns a decision this file refused twice.
    //
    // The rail entry for administration was a `<button>` that changed what the sidebar
    // showed and moved nobody, on the argument — written up at length in
    // `frontend/AGENTS.md` — that *an area is not a place*. The product owner has decided
    // otherwise: *"we agreed Administration should have its own screen — give it a route"*.
    // So the area is a place now, and this is the line that says so.
    //
    // NOTHING WAS EXEMPTED TO GET IT. The gate that refused a landing page —
    // `every route the router serves has a trail`, which resolves through `moduleFor` and
    // finds nothing for an address no destination declares — is satisfied the way it asks
    // to be satisfied: by a destination. That gate was never an argument against this
    // screen, only against a route without one.
    //
    // FIRST IN THE GROUP, because the sidebar's level one IS the group's members and a
    // drill whose overview is filed between Payroll and Recruiting is an overview nobody
    // finds. It is also what the rail entry now navigates to, so two administrators
    // pressing the same icon land on the same screen — which the `items[0]` shape it
    // replaces could not promise.
    //
    // THE LABEL IS NOT THE GROUP'S NAME, and that is not a stylistic choice: `trailOf`
    // puts the GROUP above every address, so a screen called "Administration" inside the
    // group called "Administration" prints one word twice, six pixels apart, at two sizes
    // — the exact defect that docstring measured and stops the trail at the parent to
    // avoid.
    to: "/administration",
    module: { kind: "foundation" },
    label: "nav.administration",
    icon: LayoutGridIcon,
    group: "nav.group.administration",
    // NOT A ROLE LIST. See `Requirement`'s `area` variant: what makes this screen worth
    // offering is that the caller administers more than one thing, which is the answer
    // `groupsFor` already computes and which no union of role names can restate without
    // going wrong for the caller who holds an administering role and not the scope it
    // needs.
    requires: { kind: "area", group: "nav.group.administration" },
  },

  // The surfaces with NO SELF-SERVICE in them. Every write there takes a role rather
  // than a per-endpoint judgement, so somebody without one gets a 403 on arrival.
  // Each is gated on its OWN role: onboarding and offboarding are administered by
  // different people in a real company, and a group gated as a block hands somebody
  // half a job they do not hold.
  {
    // SETTING THE COMPANY UP, which is administration and not "the company" — the
    // organisation chart beside it is a READ everybody benefits from, and this is six
    // write flows nobody without the role may use. Two destinations rather than a tab,
    // because a tab that refuses is the link-that-always-refuses defect one level down.
    to: "/setup",
    module: { kind: "licensed", module: "organization" },
    label: "nav.setup",
    icon: Building2Icon,
    group: "nav.group.administration",
    navigationFolder: "organisation-workforce",
    requires: { kind: "role", anyOf: ROLES.organization },
    // FOUR STEPS THAT WERE ONE SCROLL. The screen renders one of them at a time now,
    // chosen by the path, and the order here is the order they depend on: a position
    // names a job, a unit and a workplace, so it cannot be created until those exist.
    //
    // The sequence survives the split — it is the order of this list, which is what
    // somebody setting a company up walks down — and each step gets the whole window
    // instead of a ninth of a scroll. It also stops the screen making five requests on
    // mount to fill five tables, four of which nobody is looking at.
    //
    // THE WORKPLACE STEP IS THE SECOND ONE, and it is the only place in the product that
    // draws a register of workplaces. It briefly had a twin at `/organisation/workplaces`
    // — one object, two screens, two names ("SGK workplaces" here against "Workplaces" on
    // the rail) — which the product owner found himself and asked about twice. He settled
    // it here, with no redirect standing in for the second address: registering a
    // workplace is company setup, and it is one of the six write flows this role exists
    // for.
    //
    // THE SCREEN THAT CAME BACK IS THE SUPERSET, not the one that was here before: the
    // same three columns plus riksu's headcount and the İş Kanunu md. 30 quota per
    // province. Its reads take `riksu.read` alone and the destination takes the role, so
    // the screen is gated TIGHTER than its data — which is the safe direction, and the
    // opposite one is the leak.
    //
    // Second rather than last, because it is where it sits in the dependency order this
    // list encodes: a position names a job, a unit and a workplace.
    sections: [
      { to: "/setup/legal-entities", label: "setup.entities" },
      { to: "/setup/workplaces", label: "setup.workplaces", ownsChildren: true },
      { to: "/setup/units", label: "setup.units" },
      { to: "/setup/job-architecture", label: "jobArchitecture.heading" },
      { to: "/setup/jobs", label: "setup.jobs" },
      { to: "/setup/positions", label: "setup.positions" },
    ],
  },
  {
    // APPROVAL ROUTING, which configures a running company rather than standing one up.
    // The screen reads the exact process registry the engine booted with, so a tenant can
    // only override a certified slot and sees every step that one change will affect.
    to: "/workflow",
    module: { kind: "foundation" },
    label: "nav.workflow",
    icon: GitBranchIcon,
    group: "nav.group.administration",
    navigationFolder: "automation-integrations",
    requires: { kind: "role", anyOf: ROLES.workflow },
  },
  {
    // Mitra owns directory credentials; Riksu reconciles its directory projection.
    // SAP workforce delivery is a separate child screen so a local outbox event
    // cannot be mistaken for a provider acknowledgement.
    to: "/integrations",
    module: { kind: "foundation" },
    label: "nav.integrations",
    icon: CableIcon,
    group: "nav.group.administration",
    navigationFolder: "automation-integrations",
    requires: { kind: "role", anyOf: ROLES.organization },
    sections: [
      { to: "/integrations", label: "nav.integrations.directory", ownsChildren: true },
      { to: "/integrations/sap", label: "nav.integrations.sap" },
      { to: "/integrations/status", label: "nav.integrations.status" },
    ],
  },
  {
    // Subscription policy and issued calendar feeds are company controls. Keeping this
    // address while declaring it as its own destination lets longest-prefix resolution
    // move it to Administration without breaking links or duplicating the route.
    to: "/leave/calendar",
    module: { kind: "licensed", module: "leave" },
    label: "nav.leave.calendar",
    icon: CalendarDaysIcon,
    group: "nav.group.administration",
    navigationFolder: "automation-integrations",
    requires: { kind: "role", anyOf: ROLES.leave },
  },
  {
    // THE CALENDAR, WHICH IS ADMINISTRATION — and it spent its whole life inside a module
    // that is not. The product owner asked for this twice and has ruled.
    //
    // THE FACT THAT DECIDED IT IS RIKSU'S OWN: `riksu-api/src/leave.rs` opens with *"The
    // calendar is administration"*, and `confirm_holiday_year` checks `LEAVE_ADMINISTRATORS`
    // before it writes. Confirming a year is not a free action — migration 0051 armed a
    // trigger for exactly that write — and the screen sets the holidays and the working
    // weeks every other absence in the product is counted against. It is company
    // configuration that happens to be about leave, which is why it reads oddly as the one
    // administrative screen filed under a self-service module.
    //
    // IT IS A `Destination` AND NOT A SECTION OF `/setup`, and that is forced rather than
    // preferred. `/setup` requires `ROLES.organization` and this requires `ROLES.leave`, and
    // a module's own gate is what decides whether the rail offers it at all — so a leave
    // administrator who does not administer the org structure would not reach the module,
    // and the section inside it would be unreachable however it declared itself. That is
    // `/recruiting`'s two-server-sets problem with the worse ending: there the wider gate on
    // the module keeps both halves reachable, and here the sets do not nest at all.
    //
    // THE ADDRESS DOES NOT MOVE, and the mechanism that makes that safe is the one
    // `moduleFor` already implements: LONGEST PREFIX. `/leave/setup` is twelve characters
    // against `/leave`'s six, so this destination wins over the leave module for its own
    // address and carries its own group, its own crumb and its own gate. **`/assets/mine`'s note
    // below argues the opposite way and is not contradicted** — `/assets/mine` would have
    // been an address with NO destination of its own, so the longest DECLARED prefix would
    // have been `/assets` and it would have inherited that module's role gate. An address
    // that is itself declared inherits nothing.
    //
    // THE CENSUS LEFT THIS SCREEN ALONE AND THAT IS NOT OVERRULED HERE.
    // `docs/2026-08-12-one-destination-one-thing.md` records `/leave/setup` as *"Left. Three
    // registers, one act: setting the calendar up"* — which answers *should this be SPLIT*,
    // and this is a question about WHICH GROUP it belongs to. The screen is still one screen
    // showing one act; it has moved rail, not shape.
    //
    // `requires` IS CARRIED OVER UNCHANGED, and keeping it is the point rather than a
    // detail: it landed in `061a5b5` because the screen was offering an act the server
    // refuses to every employee in the tenant, and a move that dropped it would re-open that
    // defect while looking like a tidy-up. `ROLES.leave` mirrors `LEAVE_ADMINISTRATORS` in
    // `lib/access.ts`, so the set is riksu's and a second list here would be the copy that
    // drifts.
    //
    // SECOND IN THE GROUP, beside `/setup`, because both are configuration a company does
    // once and revisits — and everything below them is work done against it.
    to: "/leave/setup",
    module: { kind: "licensed", module: "leave" },
    label: "nav.leave.setup",
    icon: CalendarCogIcon,
    group: "nav.group.administration",
    navigationFolder: "time-pay-expense",
    requires: { kind: "role", anyOf: ROLES.leave },
    // ONE SCREEN AND THEREFORE NO `sections`, which is `Section`'s own rule. The confirmed
    // years, the holidays and the working weeks are three registers serving one act.
  },
  {
    to: "/leave/entitlements",
    module: { kind: "licensed", module: "leave" },
    label: "nav.leave.entitlements",
    icon: FileCheckIcon,
    group: "nav.group.administration",
    navigationFolder: "time-pay-expense",
    requires: { kind: "role", anyOf: ROLES.leave },
    sections: [
      {
        to: "/leave/entitlements",
        label: "nav.leave.entitlements.queue",
      },
      {
        to: "/leave/entitlements/history",
        label: "nav.leave.entitlements.history",
        ownsChildren: true,
      },
      {
        to: "/leave/entitlements/policies",
        label: "nav.leave.entitlements.policies",
      },
    ],
  },
  {
    // THE CONTRACT TERM, not somebody's attendance. It changes the effective-dated line
    // md. 41 uses to divide ordinary time, extra hours and overtime, so it belongs with
    // administrative records and is never offered to an ordinary worker.
    to: "/time/contracted-weeks",
    module: { kind: "licensed", module: "time" },
    label: "nav.timeAdministration",
    icon: TimerIcon,
    group: "nav.group.administration",
    navigationFolder: "time-pay-expense",
    requires: { kind: "role", anyOf: ROLES.contractTerm },
  },
  {
    // Limits and the company-wide exception register are policy administration, not a
    // worker's own expense workspace. The API and this destination share the same role set.
    to: "/expense/policy",
    module: { kind: "licensed", module: "expense" },
    label: "nav.expense.policy",
    icon: ReceiptIcon,
    group: "nav.group.administration",
    navigationFolder: "time-pay-expense",
    requires: { kind: "role", anyOf: ROLES.expense },
  },
  {
    // THE ADMINISTRATIVE HALF OF COMPENSATION. These screens expose company-wide pay
    // records and configure the programmes, bands and payroll inputs from which employee
    // statements are produced. Filing them under the employee's Ücret ve Yan Haklar
    // destination made a privileged caller see setup controls beside their own statement,
    // while an ordinary employee saw sections that existed only to disappear behind a
    // role check. The audience and the act are both different, so the destination is too.
    to: "/compensation/management",
    module: { kind: "licensed", module: "compensation" },
    label: "nav.compensation.management",
    icon: CircleDollarSignIcon,
    group: "nav.group.administration",
    navigationFolder: "time-pay-expense",
    requires: { kind: "role", anyOf: ROLES.compensation },
    sections: [
      {
        to: "/compensation/management/records",
        label: "nav.compensation.records",
        ownsChildren: true,
      },
      {
        to: "/compensation/management/awards",
        label: "nav.compensation.awards",
        ownsChildren: true,
      },
      {
        to: "/compensation/management/bonuses",
        label: "nav.compensation.bonuses",
        ownsChildren: true,
      },
      {
        to: "/compensation/management/merit",
        label: "nav.compensation.merit",
        ownsChildren: true,
      },
      { to: "/compensation/management/bands", label: "nav.compensation.bands" },
      { to: "/compensation/management/offer-packages", label: "offerPackages.heading" },
      {
        to: "/compensation/management/payroll-inputs",
        label: "nav.compensation.payrollInputs",
      },
      {
        to: "/compensation/management/leave-costs",
        label: "leaveCost.heading",
        module: { kind: "licensed", module: "leave" },
      },
    ],
  },
  {
    // PAYROLL PERIODS — the finance control, and the module with the sharpest role of the
    // lot. `ROLES.payroll` is `payroll.admin` and `finance.admin` and DOES NOT include
    // `people.hr`, which every other administrative destination here does: closing a month
    // establishes the instant every later correction is measured from, and the people who
    // correct HR records are exactly the people the measurement observes.
    //
    // So this is not `everyone` and it is not the personnel set either. There is no
    // self-service anywhere in the module — riksu holds the inputs a payroll run reads and
    // runs no payroll, so nothing here is about the reader's own money — and all five
    // routes take a payroll control on the server. An ordinary employee offered this
    // reaches a screen whose every control refuses.
    //
    // In `nav.group.administration` rather than beside `/time`: attendance is what riksu
    // records about somebody's working life and this is a control applied TO the record.
    to: "/payroll",
    module: { kind: "licensed", module: "payroll" },
    label: "nav.payroll",
    icon: BookLockIcon,
    group: "nav.group.administration",
    navigationFolder: "time-pay-expense",
    requires: { kind: "role", anyOf: ROLES.payroll },
    // TWO SCREENS, SPLIT ON `standing` AND ON NOTHING ELSE. The product owner asked to
    // see what is pending apart from what is finished, and the split is derived rather
    // than stored: `open` and `reopened` are months somebody still has work on — shut
    // March, or wait for the re-run and shut it again — and `closed` is the ledger every
    // later correction is measured against. There is deliberately no fourth value and no
    // status column; riksu derives the three and the browser derives nothing.
    //
    // OUTSTANDING IS THE LANDING SCREEN, because the module is opened to DO something.
    // The completed list is read when somebody is chasing a correction, which is the
    // second question and the one that starts from a month they can already name.
    //
    // Each screen carries exactly the control its rows admit — the close appears only
    // where there is no close in force, the reopen only where there is one — so the split
    // also makes the three-way standing visible as a PLACE rather than only as a word: a
    // month closed, reopened and closed again is on the completed screen, and a browser
    // deriving the standing from the two timestamps would file it under outstanding.
    sections: [
      { to: "/payroll/open", label: "nav.payroll.outstanding", ownsChildren: true },
      { to: "/payroll/closed", label: "nav.payroll.closed" },
    ],
  },
  {
    // A PLAN IS NOT THE LIVE CHART. Scenarios are counterfactual beliefs about future
    // capacity and cost; publication freezes their aggregate baseline but creates no
    // position, requisition or compensation fact. That separation is why this is its own
    // administration destination rather than a mode inside Organisation or Hiring.
    to: "/planning/scenarios",
    module: { kind: "licensed", module: "planning" },
    label: "nav.planning",
    icon: ChartNoAxesCombinedIcon,
    group: "nav.group.administration",
    navigationFolder: "organisation-workforce",
    requires: { kind: "role", anyOf: ROLES.planning },
  },
  {
    // RECRUITING. Twenty-two routes, four object types, and until now not one pixel — the
    // module a company opens to find out who has applied for a job, in a product where a
    // company could not see a single application.
    //
    // BEFORE `/onboarding`, which is the lifecycle and not a preference: this module ends at
    // an accepted offer, onboarding begins at a start date, and the two are joined by a hire
    // that belongs to neither. `awaiting-hire` is that seam drawn as a screen.
    //
    // `ROLES.recruitingSeats` AND NOT `ROLES.recruiting`, which is the wider of the two —
    // see the note on `Section.requires` and the pair of mirrors in `lib/access.ts`. The
    // hiring manager and the finance approver are routed here by an inbox item asking them
    // to authorise headcount; gating the module on the narrow set would leave them with the
    // question and no way to open the thing it is about, which is exactly the gap
    // `SEAT_READERS` was added to close.
    to: "/recruiting",
    module: { kind: "licensed", module: "recruiting" },
    label: "nav.recruiting",
    icon: UserSearchIcon,
    group: "nav.group.administration",
    navigationFolder: "people-lifecycle",
    requires: RECRUITING_AREA,
    // SEVEN SCREENS, SPLIT BY QUESTION AND BY WHO ASKS IT — the rule `/assets` and
    // `/retention` already follow, and the one this module needs most, because twenty-two
    // routes over four object types is the shape that turns into twenty-two pages.
    //
    // Recruiting is THREE OBJECTS ON ONE ARROW rather than one funnel: a requisition is the
    // authorisation to spend headcount, a recruit is the posting-application-offer chain
    // with its OWN approval, and a hire is a separate transaction belonging to the people
    // module. Ordering these five as the arrow would draw the funnel the model refuses, so
    // they are ordered by WHO OPENS THEM AND HOW OFTEN:
    //
    //   "who applied, and where are they"   the recruiter, every working day. It is the
    //                                       landing screen because it is the daily work and
    //                                       because it is the sentence this module's own
    //                                       ranking turns on — a company that cannot see
    //                                       its applications does not need a page for its
    //                                       adverts.
    //   "what have we offered"              the recruiter again, and the compensation
    //                                       approver. The money and the settlement live
    //                                       here, and an offer carries its own approval
    //                                       chain, which is what makes it its own screen
    //                                       rather than a column on the pipeline.
    //   "who thinks they have a job"        HR, and whoever creates the employment. This is
    //                                       the report the module exists to make safe: an
    //                                       accepted offer creates NO employment, and the
    //                                       gap is only tolerable while it is visible.
    //   "which seats are authorised"        the hiring manager and finance. The seat half,
    //                                       and the only two screens the wider role list
    //                                       reaches.
    //   "what have we advertised"           the recruiter, occasionally. Last for
    //                                       `/assets`' catalogue reason: it is a register
    //                                       rather than a question, and nobody opens this
    //                                       module to read it.
    //
    // The careers section administers the public board: open or rename its address,
    // publish an existing internal posting, revise or withdraw it, and inspect the
    // submissions that arrived through that publication. It takes the narrow recruiter
    // role list because it exposes candidate identities and public-facing copy.
    sections: [
      {
        to: "/recruiting/candidates",
        label: "recruiting.pool.heading",
        requires: RECRUITING_PIPELINE,
        ownsChildren: true,
      },
      {
        to: "/recruiting/applications",
        label: "nav.recruiting.pipeline",
        requires: RECRUITING_PIPELINE,
        ownsChildren: true,
        childRequires: { kind: "role", anyOf: ROLES.recruitingEvaluators },
      },
      {
        to: "/recruiting/interviews",
        label: "recruiting.calendar.heading",
        requires: RECRUITING_PIPELINE,
      },
      { to: "/recruiting/offers", label: "nav.recruiting.offers", requires: RECRUITING_PIPELINE },
      {
        to: "/recruiting/awaiting-hire",
        label: "nav.recruiting.awaitingHire",
        requires: RECRUITING_PIPELINE,
      },
      // NO `requires` ON THE SEAT HALF, and that is the member doing its job rather than
      // being forgotten: these two take exactly what the module takes, so repeating it here
      // would be a second copy of one fact and the two would drift. The module's gate IS
      // their gate.
      {
        to: "/recruiting/requisitions",
        label: "nav.recruiting.requisitions",
        requires: { kind: "role", anyOf: ROLES.recruitingSeats },
        ownsChildren: true,
        childRequires: { kind: "role", anyOf: ROLES.requisitionAuthors },
      },
      {
        to: "/recruiting/requisitions/reports",
        label: "recruiting.reports.heading",
        report: true,
        requires: { kind: "role", anyOf: ROLES.requisitionReportReaders },
      },
      {
        to: "/recruiting/postings",
        label: "nav.recruiting.postings",
        requires: { kind: "role", anyOf: ROLES.recruitingSeats },
        ownsChildren: true,
      },
      {
        to: "/recruiting/careers",
        label: "nav.recruiting.careers",
        requires: RECRUITING_PIPELINE,
        ownsChildren: true,
      },
      // AND THE REGISTER IS LAST, which is `/assets`' catalogue reason rather than a ranking
      // of importance: nobody opens this area to read it, and the people who do open it for
      // the register reach it from a compliance question rather than from the daily work.
      //
      // It carries its own `requires` for the reason the pipeline sections do — the module's
      // gate is the UNION of two sets, so a finance approver reaching the area would
      // otherwise be offered a register riksu refuses them, which is the
      // link-that-always-refuses defect this whole file was written against.
      {
        to: "/recruiting/process-plans",
        label: "processPlans.heading",
        requires: RECRUITING_PIPELINE,
      },
      {
        to: "/recruiting/agencies",
        label: "nav.recruiting.agencies",
        requires: RECRUITING_AGENCIES,
      },
    ],
  },

  {
    to: "/onboarding",
    module: { kind: "licensed", module: "onboarding" },
    label: "nav.onboarding",
    icon: FileCheckIcon,
    // Company-wide joining work belongs to the employee lifecycle in Administration.
    // Participant plans and questionnaires live in the existing inbox task hierarchy.
    group: "nav.group.administration",
    navigationFolder: "people-lifecycle",
    requires: { kind: "role", anyOf: ROLES.onboarding },
    /**
     * TWO SECTIONS BECAUSE THE DOMAIN IS TWO NATURES, and riksu decided that before the
     * screen existed. `docs/2026-07-31-onboarding-is-two-natures.md` splits pre-boarding
     * from orientation into *"two tables and two types. Not one with a `statutory` flag"*
     * and *"two queries, because they have two audiences"* — and the census at
     * `docs/2026-08-12-one-destination-one-thing.md:243` named this screen as the one
     * still holding both: *"four blocks over two subjects: who is joining, and the ledger
     * of missed and late statutory deadlines."* It was recorded and left open.
     *
     * The nav carried neither. This destination was six lines with no reasoning in a file
     * where every other judgement is argued, its label named the ledger alone, and the
     * OTHER nature — the nine-rung induction walk at `/onboarding/joining/{id}` — was
     * reachable only from a row action, invisible from the rail, and the larger of the
     * two.
     *
     * EXPLICIT `/onboarding/deadlines` RATHER THAN `:section`. Not for a collision: React
     * Router matches by segment count, so a one-segment `:section` could never capture the
     * two-segment `/onboarding/joining/:employment_id`. It is so that `ROUTE_PATHS` — what
     * the trail gate walks — names both addresses, which a single parameterised route
     * leaves it unable to do for the module's own.
     */
    sections: [
      {
        to: "/onboarding/joiners",
        label: "nav.onboarding.joiners",
        ownsChildren: true,
        childNamespaces: ["/onboarding/joining"],
        requires: { kind: "role", anyOf: ROLES.onboarding },
      },
      {
        to: "/onboarding/documents",
        label: "candidate.adminHeading",
        ownsChildren: true,
        requires: { kind: "role", anyOf: ROLES.candidateDocuments },
      },
      {
        to: "/onboarding/forms",
        label: "forms.heading",
        requires: { kind: "role", anyOf: ROLES.onboarding },
      },
      { to: "/onboarding/support", label: "nav.onboarding.support", ownsChildren: true },
      {
        to: "/onboarding/reviews",
        label: "development.heading",
        ownsChildren: true,
        module: { kind: "licensed", module: "performance" },
      },
      {
        to: "/onboarding/final-reviews",
        label: "finalReview.heading",
        ownsChildren: true,
        module: { kind: "licensed", module: "performance" },
      },
      {
        to: "/onboarding/safety/programs",
        label: "safety.programsHeading",
        ownsChildren: true,
        requires: { kind: "role", anyOf: ROLES.safety },
      },
      {
        to: "/onboarding/safety/assessments",
        requires: { kind: "role", anyOf: ROLES.onboarding },
        label: "safety.assessmentsHeading",
        ownsChildren: true,
        childNamespaces: ["/onboarding/safety/employments"],
      },
      {
        to: "/onboarding/deadlines",
        label: "nav.onboarding.deadlines",
        requires: { kind: "role", anyOf: ROLES.onboarding },
      },
      // AND THE REGISTER, which is the third answer rather than a view of the other two.
      // `deadlines` draws what is still ahead and what was missed; between them a
      // declaration FILED ON TIME appears on neither, so the company's good record had
      // nowhere to be read and neither did a worker's *was I declared*.
      //
      // Keep the filing administration in the HR navigation. Personal evidence is read
      // through its existing authorized record routes.
      {
        to: "/onboarding/declarations",
        label: "nav.onboarding.declarations",
        requires: { kind: "role", anyOf: ROLES.onboarding },
      },
    ],
  },
  {
    // ONE SCREEN AND THEREFORE NO `sections`, said out loud because the ABSENCE of a reason
    // is what made this read as an oversight. Every other judgement in this file is argued;
    // this destination was five bare lines beside modules carrying paragraphs, and the
    // product owner reasonably read the flatness as something nobody had got to.
    //
    // It is not. riksu serves exactly ONE tenant-wide offboarding read — the contract calls
    // it *"Every leaving still running"* — and both other operations are per-leaving. A
    // second section would be an address with nothing behind it, which `Section`'s own rule
    // refuses and which this file already refuses by name for the assets section that does
    // not exist. The two candidates are unbuilt and `tools/check-parity.mjs` records why:
    // the cross-leaver work-certificate list *"which nobody has asked for… md. 28 names no
    // period, so there is no deadline report to draw"*, and the checklist rescission, *"a
    // row action on the leaver's checklist, which has no screen of its own"*.
    //
    // A leaving's own stages exist one level down, in `leaving-record.tsx`, and already have
    // an address. AND THE DAY A SECTION IS ADDED, `records.ts` MOVES WITH IT: it notes that
    // `/offboarding/{id}` is a prefix-and-one-segment match that "cannot collide with
    // anything the router serves" — true only while there are no sections, because the first
    // one gives this module a two-segment address of its own.
    to: "/offboarding",
    module: { kind: "licensed", module: "offboarding" },
    label: "nav.offboarding",
    icon: DoorOpenIcon,
    group: "nav.group.administration",
    navigationFolder: "people-lifecycle",
    requires: { kind: "role", anyOf: ROLES.offboarding },
  },
  {
    // ZİMMET. Eleven operations and, until now, no pixel — the largest module in the
    // product with no screen, which is how "zimmet kısmı nerede?" gets asked.
    //
    // `ROLES.assets` and not `everyone`: this is the ADMINISTRATION half of the module.
    // Every write here is `require_any_role(ASSET_ADMINISTRATORS)` on the server — handing
    // out, taking back and raising a claim — so an ordinary employee would reach screens
    // whose controls refuse. Their standing answer lives at the separate, possessive
    // `/assets/mine` destination above; a shared data module does not require a shared
    // audience or gate.
    to: "/assets",
    module: { kind: "licensed", module: "assets" },
    label: "nav.assets",
    icon: PackageIcon,
    group: "nav.group.administration",
    navigationFolder: "records-compliance",
    requires: { kind: "role", anyOf: ROLES.assets },
    // FOUR SCREENS, and the split is by WHO IS ASKING rather than by which table a
    // row lives in. Three of the four are the three questions this module answers, and
    // they belong to three different people on three different days:
    //
    //   "what is out on loan"          the assets manager, and the offboarding clerk
    //                                  chasing a leaver. Daily, operational, and the
    //                                  module's own name — so it is the landing screen
    //                                  rather than the catalogue, which is what a
    //                                  dependency ordering would have put first.
    //   "what would not survive a
    //    challenge"                    whoever will have to PRODUCE the artifact. It is
    //                                  second, immediately under the list it reports on,
    //                                  because the person who can still fix a gap is the
    //                                  person who made it — and because the day it is
    //                                  read for the first time must not be the day of
    //                                  the hearing.
    //   "what is owed, and what may
    //    lawfully come out of a wage"  whoever asserts damage, and the two reviewers
    //                                  behind them. It is the only screen here with a
    //                                  statute on it and it is kept apart for that
    //                                  reason: choosing between TBK md. 407/2's two
    //                                  grounds is not a menu item on a row.
    //
    // The catalogue owns original equipment records. The company report aggregates
    // their dated counts and separately sourced financial values; it edits neither
    // register and does not infer a depreciation or replacement policy.
    //
    // "What came back" is the fourth question and it has NO SCREEN, because riksu
    // answers no read for it: `GET /v1/assets/holdings` returns only what is still out.
    // The custody screen carries an as-at date instead, and says so in as many words.
    // A fifth section over an endpoint that does not exist would be a place with
    // nothing in it.
    sections: [
      { to: "/assets/holdings", label: "nav.assets.holders" },
      { to: "/assets/evidence-gaps", label: "nav.assets.gaps" },
      { to: "/assets/damage-claims", label: "nav.assets.damage" },
      { to: "/assets/catalogue", label: "nav.assets.catalogue", ownsChildren: true },
      { to: "/assets/reports", label: "assets.reports.heading", report: true },
    ],
  },
  {
    // THE DOCUMENT REGISTER — what is unsigned, and what riksu is still waiting for bytes
    // for. Eight routes under `/v1/documents/*` and the only pixel any of them ever reached
    // was the özlük dosyası section on a person's record, which lists ONE person's file.
    //
    // A TENANT-WIDE REPORT, WHICH IS WHY IT IS A DESTINATION AND NOT A SECTION. "Which
    // documents are unsigned" and "whose upload never arrived" are asked ACROSS people —
    // before a payroll close, or when an induction step still reads `issued` — and a section
    // on a record answers them one file at a time, which is the shape that makes a company
    // export the question to a spreadsheet.
    //
    // `ROLES.documents` and NOT `everyone`, and this is the one destination here whose gate
    // is not a mirror of a gate riksu enforces. Neither chase list takes a role at all: both
    // are `Access::Scope(scope::READ)` and narrow through `visible_people`, so a caller
    // holding nothing is shown their own subtree rather than refused. That is a real answer
    // and it is still not a screen — `POST /v1/documents/signatures` and the verification
    // write are both headless, so what an ordinary employee would meet is a short report
    // with no control on it about documents somebody else has to act on. The role is what
    // makes this the tenant-wide list it is named as, and `Documents` says so in words to
    // anybody who reaches the address without it.
    //
    // ONE SCREEN AND THEREFORE NO `sections`, which is `Section`'s own rule rather than an
    // omission: the two chase lists are two TABLES on one page, because riksu keeps them
    // apart precisely so that nothing falls off both — and a reader chasing documents wants
    // both in view rather than a sidebar between them.
    //
    // IN `nav.group.administration` beside onboarding and retention. Filing a document is an
    // administrative act — `POST /v1/documents` takes a role and a document filed by its own
    // subject is a document whose subject chose what it says — and there is no self-service
    // anywhere in this module.
    to: "/documents",
    module: { kind: "foundation" },
    label: "nav.documents",
    icon: FileSignatureIcon,
    group: "nav.group.administration",
    navigationFolder: "records-compliance",
    requires: { kind: "role", anyOf: [...ROLES.documents, ...ROLES.documentTemplates] },
  },
  {
    // The administrative half is a separate destination because the worker's own
    // posture does not imply authority to see the company fleet or set collection policy.
    to: "/device-security",
    module: { kind: "licensed", module: "device_security" },
    label: "nav.deviceSecurity",
    icon: ShieldCheckIcon,
    group: "nav.group.administration",
    navigationFolder: "records-compliance",
    requires: { kind: "role", anyOf: ROLES.deviceSecurity },
    sections: [
      { to: "/device-security/devices", label: "nav.deviceSecurity.devices", ownsChildren: true },
      { to: "/device-security/policy", label: "nav.deviceSecurity.policy" },
    ],
  },
  {
    to: "/privacy/field-permissions",
    module: { kind: "licensed", module: "people" },
    label: "nav.fieldPermissions",
    icon: ShieldCheckIcon,
    group: "nav.group.administration",
    navigationFolder: "records-compliance",
    requires: { kind: "role", anyOf: ROLES.disclosureReaders },
  },
  {
    // THE PERIODIC DESTRUCTION REVIEW. KVKK md. 7's duty is discharged by the review being
    // CONDUCTED rather than by the schedule existing — riksu has held a complete schedule
    // since migration 0048 and had performed the review zero times, because nothing anywhere
    // could ask it to. Five routes and no pixel: a six-monthly statutory duty dischargeable
    // only by whoever can compose an HTTP request is one most customers will not discharge.
    //
    // `ROLES.retention` and NOT `everyone`, and not the audit set either. Every one of the
    // five routes is `Subject::Role` — a surface with no self-service anywhere in it, where
    // "am I this person" is never the question — so an ordinary employee arriving here would
    // meet a screen whose every control refuses.
    //
    // LAST IN THE GROUP, which is a claim about frequency rather than importance: this is
    // opened twice a year, and the four above it are opened on ordinary working days. The
    // rail's order is what somebody scans, and a six-monthly duty at the top of it would
    // push the daily work down.
    to: "/retention",
    module: { kind: "foundation" },
    label: "nav.retention",
    icon: FileClockIcon,
    group: "nav.group.administration",
    navigationFolder: "records-compliance",
    // THIS DECLARED `andScope: SCOPES.auditRead` AND MUST NOT AGAIN — the history is here
    // because the 403 that produced it is exactly what a future reader will meet first.
    //
    // It was measured against the running stack: a token holding `people.hr` and not the
    // scope was offered this screen and met *"this route requires the riksu.audit.read
    // scope"* on every read. The conclusion drawn then was that the DESTINATION should hide
    // itself, and `andScope` was built for it. **The scope was the defect.** Every
    // `/v1/retention/*` READ required `riksu.audit.read` while every WRITE required only
    // `riksu.write`, so with the scope the BFF actually mints a signed-in holder of
    // `people.hr` could record a destruction disposition and could not read the queue
    // saying what would be destroyed.
    //
    // `5e55fb6` moved the three reads to `riksu.read`, leaving
    // `require_any_role(DESTRUCTION_ADMINISTRATORS)` to answer *is this your job* — so the
    // role half below is now the whole gate, and re-adding a scope here would hide the
    // screen from everybody again while the routes permit it.
    requires: { kind: "role", anyOf: ROLES.retention },
    // TWO SCREENS, and the split is by WHO IS ASKING — the same rule `/assets` follows.
    // Conducting the review is a working surface: the schedule, what each class makes due,
    // and a decision per table. The records are EVIDENCE, read months later by somebody
    // answering a regulator, and what they need is which reviews happened and what each
    // decided — including what was decided not to destroy.
    //
    // The review is the landing screen because the module is opened to DO something; the
    // records are the second question and are asked by somebody who already knows they are
    // looking for a past review.
    //
    // THE HOLDS SIT BETWEEN THEM, and that is a claim about WHEN rather than about
    // importance. A hold is placed the day a dispute or an inspection starts, which is
    // while the schedule is running; the records are read afterwards, usually for somebody
    // outside. `check-parity.mjs` had declared the register "belongs beside the review
    // queue on the same page", written before the page existed — read against it, a second
    // fifty-row table under a fifty-row queue would make the working surface two scrolling
    // registers. What that entry was right about is the RELATIONSHIP, and it is kept: the
    // queue's held-back figure says on its hover that a hold beats the schedule and names
    // this screen as where the standing ones are.
    sections: [
      { to: "/retention/reviews", label: "nav.retention.queue" },
      { to: "/retention/holds", label: "nav.retention.holds" },
      { to: "/retention/records", label: "nav.retention.records" },
    ],
  },
];

/**
 * The destination the address in view belongs to, or `undefined`.
 *
 * LONGEST PREFIX, so `/leave/requests` resolves to the leave module rather than to `/` —
 * which every path is a prefix of, and which would otherwise win for all of them.
 *
 * IT IS ALSO WHAT LETS A DESTINATION SIT UNDER ANOTHER ONE'S PATH. `/leave/setup` is a
 * destination in its own right and `/leave` is a prefix of it, and the longer one wins — so
 * the calendar carries its own group, crumb and role gate while living at an address that
 * starts with a module it does not belong to. An address with no destination of its own
 * still resolves to the nearest one that has, which is why `/assets/mine` is not `/assets/mine`.
 *
 * BOTH LISTS. The two rails are separate on purpose — a destination is in one or the
 * other, never both — but that is a question about where a link is DRAWN, and this
 * answers "which module is this address in". Searching only `DESTINATIONS` left `/inbox`
 * and `/notifications` resolving to nothing, and every screen-level answer built on this
 * then had a hole in it exactly two screens wide.
 */
export function moduleFor(pathname: string): Destination | undefined {
  let best: Destination | undefined;
  let bestLength = -1;
  for (const destination of [...DESTINATIONS, ...ALONGSIDE]) {
    for (const prefix of [destination.to, ...(destination.sections?.map((one) => one.to) ?? [])]) {
      const owns = pathname === prefix || (prefix !== "/" && pathname.startsWith(`${prefix}/`));
      if (owns && prefix.length > bestLength) {
        best = destination;
        bestLength = prefix.length;
      }
    }
    for (const entrance of destination.entrances ?? []) {
      if (pathname === entrance && entrance.length > bestLength) {
        best = destination;
        bestLength = entrance.length;
      }
    }
  }
  return best;
}

/**
 * Which of a module's sections the address IS, or `undefined`.
 *
 * Exact unless the section explicitly owns child records. A blanket prefix match would
 * make `/setup/positions/new` claim to be the positions screen, so child ownership stays
 * in the declaration. The longest eligible prefix wins: `/travel/sent/{id}` belongs to
 * Sent even though the landing section at `/travel` also owns its request details.
 *
 * One function because two readers ask the same question: the sidebar highlights the
 * screen you are on and the trail names it as the place you are. They were the same
 * `section.to === pathname` written twice, which is how a highlight and a heading end up
 * disagreeing about which screen is showing.
 */
function sectionMatches(section: Section, pathname: string): boolean {
  return (
    section.to === pathname ||
    (section.ownsChildren === true &&
      [section.to, ...(section.childNamespaces ?? [])].some((namespace) =>
        pathname.startsWith(`${namespace}/`),
      ))
  );
}

/** The declared section hierarchy containing an address, from root to leaf. */
export function sectionPathFor(
  destination: Destination,
  pathname: string,
): readonly Section[] | undefined {
  function inside(sections: readonly Section[]): readonly Section[] | undefined {
    const candidates: (readonly Section[])[] = [];
    for (const section of sections) {
      const child = section.children ? inside(section.children) : undefined;
      if (child) candidates.push([section, ...child]);
      if (sectionMatches(section, pathname)) candidates.push([section]);
    }
    return candidates.sort((left, right) => {
      const leftLeaf = left.at(-1)?.to.length ?? 0;
      const rightLeaf = right.at(-1)?.to.length ?? 0;
      return rightLeaf - leftLeaf || right.length - left.length;
    })[0];
  }

  return destination.sections ? inside(destination.sections) : undefined;
}

export function sectionFor(destination: Destination, pathname: string): Section | undefined {
  return sectionPathFor(destination, pathname)?.at(-1);
}

/**
 * Whether a caller satisfies a requirement.
 *
 * Separate from `canReach` because `creations.ts` asks the same question about a
 * different noun — "may I be OFFERED this create flow" — and a second copy of two lines
 * is how the rail and the create menu end up disagreeing about one role. The requirement
 * vocabulary is the shared thing; what carries it is not.
 */
export function permits(requires: Requirement, held: Held | undefined): boolean {
  if (requires.kind === "everyone") {
    return true;
  }
  if (requires.kind === "reports") return reportsFor(held).length > 0;
  // AN AREA'S LANDING IS REACHABLE WHEN THE AREA IS, and the area is what `groupsFor`
  // already decides. Counted here rather than mirrored as a union of role names, because a
  // union answers a slightly different question and answers it wrongly in the offering
  // direction — see the `area` variant's own note.
  //
  // `kind !== "area"` IS WHAT MAKES THIS TERMINATE, and it is a structural guarantee rather
  // than a fact about today's declaration: a landing never counts another landing, so no
  // arrangement of them can recurse.
  if (requires.kind === "area") {
    return (
      DESTINATIONS.filter(
        (destination) =>
          destination.group === requires.group &&
          destination.requires.kind !== "area" &&
          canReach(destination, held),
      ).length > 1
    );
  }
  // BOTH, AND THE ORDER IS NOT THE POINT — the conjunction is. riksu checks the scope in
  // middleware and the role in the handler, so a caller satisfying one and not the other is
  // refused; offering them the destination is the defect this member exists to close.
  //
  // A MISSING CLAIM IS *HOLDS NOTHING* on both axes. An older server's session document may
  // carry neither, and the safe reading hides the destination — never shows it because we
  // could not tell, which is the failure direction that puts somebody in front of a screen
  // whose every control refuses.
  return (
    holdsAny(held?.roles, requires.anyOf) &&
    (requires.andScope === undefined || holdsScope(held, requires.andScope))
  );
}

/** Whether this caller can reach a destination. */
export function moduleIsEnabled(destination: Destination, held: Held | undefined): boolean {
  return destination.module.kind === "foundation" || holdsModule(held, destination.module.module);
}

/** Whether this caller can reach a destination. */
export function canReach(destination: Destination, held: Held | undefined): boolean {
  if (!moduleIsEnabled(destination, held)) {
    return false;
  }
  return permits(destination.requires, held);
}

function sectionReachable(
  destination: Destination,
  section: Section,
  held: Held | undefined,
): boolean {
  if (!sectionModuleIsEnabled(destination, section, held)) {
    return false;
  }
  return section.requires === undefined || permits(section.requires, held);
}

function sectionModuleIsEnabled(
  destination: Destination,
  section: Section,
  held: Held | undefined,
): boolean {
  const module = section.module ?? destination.module;
  return module.kind === "foundation" || holdsModule(held, module.module);
}

/**
 * Whether the caller may open this exact address, including a section's own module.
 *
 * A child-specific requirement is a complete gate for that child. It may therefore admit
 * somebody who cannot reach the register or module navigation above it, while the licensed
 * product boundary and every ancestor section remain enforced. This is how an assigned
 * evaluator reaches one application without acquiring a candidate-list permission.
 */
export function canReachPath(
  destination: Destination,
  pathname: string,
  held: Held | undefined,
): boolean {
  const path = sectionPathFor(destination, pathname);
  const leaf = path?.at(-1);
  const childRequirement =
    leaf?.ownsChildren === true && pathname !== leaf.to ? leaf.childRequires : undefined;

  if (childRequirement !== undefined) {
    if (!moduleIsEnabled(destination, held)) {
      return false;
    }
    return (
      path?.every((section) =>
        section === leaf
          ? sectionModuleIsEnabled(destination, section, held) && permits(childRequirement, held)
          : sectionReachable(destination, section, held),
      ) ?? false
    );
  }

  if (!canReach(destination, held)) {
    return false;
  }
  return (
    path === undefined || path.every((section) => sectionReachable(destination, section, held))
  );
}

export interface ReportEntrance {
  readonly to: string;
  readonly label: MessageKey;
  readonly moduleLabel: MessageKey;
}

/** Original leaf gates own discovery too. Derived entrances never discover one another. */
export function reportsFor(held: Held | undefined): readonly ReportEntrance[] {
  const reports: ReportEntrance[] = [];
  for (const destination of DESTINATIONS) {
    if (destination.requires.kind === "reports" || destination.requires.kind === "area") continue;
    const visit = (sections: readonly Section[]) => {
      for (const section of sections) {
        if (section.requires?.kind === "reports" || section.requires?.kind === "area") continue;
        if (section.children) visit(section.children);
        else if (section.report && canReachPath(destination, section.to, held))
          reports.push({ to: section.to, label: section.label, moduleLabel: destination.label });
      }
    };
    visit(destination.sections ?? []);
  }
  return reports;
}

/** The optional overview leaf inside an area; the area parent itself never navigates. */
export function landingOf(group: RailGroup): Destination | undefined {
  return group.items.find((item) => item.requires.kind === "area");
}

/**
 * THE AREA'S MODULES, without the landing that stands for the area itself.
 *
 * The landing lists these, and a landing listing itself is a link to the page you are on.
 * The same predicate `permits` counts with, so the two cannot disagree about what a member
 * is.
 */
export function membersOf(group: RailGroup): readonly Destination[] {
  return group.items.filter((item) => item.requires.kind !== "area");
}

/**
 * THE SAME DESTINATION, CARRYING ONLY THE SECTIONS THIS CALLER CAN REACH.
 *
 * The one place `Section.requires` is consulted, so the sidebar, the section highlight and
 * the breadcrumb are narrowed by construction rather than each remembering to ask — the
 * shape `canReach` already gives the rail one level up, and the reason `module-nav.tsx`
 * derives its sections from the module rather than being handed a list.
 *
 * IT ANSWERS THE SAME OBJECT WHERE NOTHING IS NARROWED, which is not a micro-optimisation:
 * `ModuleNavProvider`'s value is memoized on the module's identity, and a fresh object per
 * render would re-render the sidebar, the breadcrumb and the whole content grid on every
 * navigation — the defect `shell-render.test.tsx` was written to pin. EVERY destination but
 * ONE declares no `requires` on any section, so all of them take this path: the exception is
 * `/recruiting`, whose two halves are gated by two server sets.
 *
 * `/leave` WAS THE SECOND until the calendar setup left it for `nav.group.administration`,
 * and the mechanism is deliberately kept rather than reduced to one call site: a section
 * gated apart from its module is a real shape riksu produces whenever one module's screens
 * answer to two server sets, and `destinations.test.ts` holds it against `/recruiting`.
 *
 * A module whose every section is refused keeps its rail item and shows no sidebar, which
 * is `Destination.sections`' own absent case and reads correctly: the module is reachable
 * (its own `requires` said so) and has one screen's worth of it for this caller. It cannot
 * happen today — the seat half is gated by the module's own requirement — and the day a
 * module gates every section, an empty sidebar is a better answer than a list of links
 * that refuse.
 */
export function narrowedFor(destination: Destination, held: Held | undefined): Destination {
  const sections = destination.sections;
  if (sections === undefined) {
    return destination;
  }

  function narrow(section: Section): Section | null {
    if (!sectionReachable(destination, section, held)) return null;
    if (!section.children) return section;
    const children = section.children.map(narrow).filter((child) => child !== null);
    return children.length === section.children.length ? section : { ...section, children };
  }

  const reachable = sections.map(narrow).filter((section) => section !== null);
  const unchanged = reachable.every((section, index) => section === sections[index]);
  return unchanged && reachable.length === sections.length
    ? destination
    : { ...destination, sections: reachable };
}

/** ONE LEVEL of the trail. `to` is absent for a level that is not a place. */
export interface Crumb {
  readonly label: MessageKey;
  readonly to?: string;
}

/**
 * WHERE AN ADDRESS SITS, as the levels above it.
 *
 * # It is the ANCESTORS, and the page's own `h2` is the leaf
 *
 * This is the decision to argue with rather than to change quietly. The trail stops at
 * the PARENT: `/setup/positions` reads "Yönetim › Şirket kurulumu" over an `h2` that
 * says "Pozisyonlar", and `/people` reads "Şirket" over an `h2` that says "Kişiler".
 *
 * The alternative — ending the trail on the page you are on — was measured against the
 * catalogue rather than guessed at, and it prints the same sentence twice on almost every
 * screen in the product: `people.heading` is "Kişiler" and `nav.people` is "Kişiler";
 * `leave.heading` and `nav.leave` are both "İzin"; `org.heading` and `nav.organisation`
 * are both "Organizasyon"; `setup.positions` and the setup screen's own heading are both
 * "Pozisyonlar". Two copies of one word six pixels apart, at 12px and at 20px, is what
 * makes a header look padded — and it buys nothing, because the `h2` never stopped saying
 * which screen this is. What went missing when `/setup` became five addresses was the
 * level ABOVE it, and that is exactly what this restores.
 *
 * The consequence for the ARIA pattern is deliberate and is not an omission: nothing in
 * the list is the current page, so nothing carries `aria-current="page"`. The WAI-ARIA
 * breadcrumb pattern conditions that attribute on the trail containing a link to the
 * current page — "if the last link is the current page" — and here the last link never
 * is. A crumb marked as the current page while pointing at the module above it would be
 * a claim that is simply false.
 *
 * # The GROUP is a level, and it is not a place
 *
 * Every destination declares one and the rail is built out of them, so "Yönetim" and
 * "Şirket" are the product's own top level rather than a label invented for this. It
 * carries no `to`, because there is no page that lists a group — and a crumb that
 * navigated somewhere it only looks like it should is worse than one that does nothing.
 * It is what makes the trail non-empty on EVERY screen, including the ones with a single
 * flat module, which is the whole reason the group is here rather than a "home" crumb
 * pointing at `/`.
 *
 * A durable detail adds the section that owns it as a third ancestor. That relationship
 * comes from the same declaration the sidebar reads, never from a screen passing crumbs
 * or inventing a return address.
 */
export function trailOf(module: Destination | null | undefined, pathname: string): Crumb[] {
  if (!module) {
    return [];
  }
  // THE UTILITY RAIL IS PLACEMENT, not information architecture. "Alongside your work"
  // says where the icon sits in the shell; putting it in a breadcrumb made a message read
  // as a child of a layout instruction. Inbox is the semantic parent of Tasks, Messages
  // and Notices, so it is the root for this one destination. On the tasks landing it is a
  // non-link ancestor label; on its children it returns to the inbox landing.
  if (module.group === "nav.utility") {
    const entry = entryPath(module);
    const trail: Crumb[] = [
      pathname === entry ? { label: module.label } : { label: module.label, to: entry },
    ];
    const path = sectionPathFor(module, pathname) ?? [];
    for (const section of path) {
      const exactLeaf = section === path.at(-1) && pathname === section.to;
      if (section.children) {
        trail.push({ label: section.label });
      } else if (section.to !== module.to && !exactLeaf && trail.at(-1)?.to !== section.to) {
        trail.push({ label: section.label, to: section.to });
      }
    }
    return trail;
  }
  const trail: Crumb[] = [{ label: module.group }];
  // NOT ON THE MODULE'S OWN ADDRESS. `/leave` is the leave module and its balances
  // section at one address, so a module crumb there would point at the page being looked
  // at — and the `h2` on that screen already says "İzin".
  const entry = entryPath(module);
  if (pathname !== entry) {
    trail.push({ label: module.label, to: entry });
  }
  const path = sectionPathFor(module, pathname) ?? [];
  for (const section of path) {
    if (section.children) {
      trail.push({ label: section.label });
    } else if (
      pathname !== section.to &&
      section.to !== module.to &&
      trail.at(-1)?.to !== section.to
    ) {
      trail.push({ label: section.label, to: section.to });
    }
  }
  return trail;
}

/**
 * The trail for an address, narrowed to what this caller can reach.
 *
 * The courtesy is the rail's, one level down: a caller who cannot reach `/setup` is not
 * told they are inside it, because a name for a place that refuses on arrival is the
 * link-that-always-refuses defect with the link taken off. The server refuses regardless.
 *
 * The two steps the shell takes, composed into one. The shell performs them separately
 * because it also needs the `Destination` itself for the module sidebar; this is what the
 * gate walks every route with, and `shell.test.tsx` is what pins the shell's pair to it.
 */
export function trailFor(pathname: string, held: Held | undefined): Crumb[] {
  const found = moduleFor(pathname);
  return trailOf(found && canReach(found, held) ? found : null, pathname);
}

/** The destinations this caller can reach, in rail order. */
export function reachable(held: Held | undefined): Destination[] {
  return DESTINATIONS.filter((destination) => canReach(destination, held));
}

/**
 * ONE GROUP AS THE RAIL DRAWS IT: what it is called, what is in it for this caller, and
 * whether it is one entry that opens or its members side by side.
 */
export interface RailGroup extends Group {
  readonly items: readonly Destination[];
  /**
   * WHETHER THIS GROUP IS ONE ENTRY IN THE RAIL, WITH ITS MEMBERS IN THE MODULE SIDEBAR.
   *
   * # It behaves like a MODULE, which is the correction that produced it
   *
   * The first build made this a menu anchored to the rail. The product owner refused it on
   * sight — *"sen context menu yapmışsın, popover mı oluyor artık neyse… yine sidebar +
   * left rail şeklinde olacaktı"* — and he is asking for the shape the product already uses
   * everywhere: pressing an entry fills the `module` grid area with what is inside it. So a
   * collapsed group is not a new interaction at all. It is a module whose screens happen to
   * be other modules.
   *
   * # Derived HERE and not in any renderer
   *
   * Three surfaces read it — the icon rail, the module sidebar and the sheet — and each
   * draws it differently because each has different room. If any of them decided FOR ITSELF
   * *whether* a group collapses, they would answer differently the day the rule moved. They
   * differ in what they draw, never in what they are drawing.
   */
  readonly collapsed: boolean;
}

/**
 * The reachable destinations, grouped, with empty groups dropped.
 *
 * # An EMPTY group renders nothing at all, and that is load-bearing now
 *
 * A heading over nothing was always a promise the product does not keep; a collapsed group
 * makes it worse, because the promise gains an ADDRESS. Somebody holding no administering
 * role would get a rail entry called "Yönetim" pointing at `items[0].to` — and there is no
 * `items[0]`, so the entry either has no address at all or refuses on arrival. That is the
 * link-that-always-refuses defect this whole file exists against. So a group with no
 * reachable member is dropped before anything can draw it, exactly as before.
 *
 * # A group with ONE reachable member is drawn as that member
 *
 * `collapsed` is false there, so a caller holding `finance.admin` alone sees the payroll
 * icon under its own name rather than a "Yönetim" entry whose sidebar lists one thing. This
 * is `Section`'s own rule one level up — a module with one screen has nothing to navigate
 * between — and the collapsed version additionally replaces the name of the only screen
 * they hold with the word for the set it belongs to. It is one comparison to change if the
 * rail's shape should instead be identical for every caller.
 */
export function groupsFor(held: Held | undefined): RailGroup[] {
  const byGroup = new Map<GroupLabel, Destination[]>();

  for (const destination_ of reachable(held)) {
    const destination = narrowedFor(destination_, held);
    const held_ = byGroup.get(destination.group);
    if (held_) {
      held_.push(destination);
    } else {
      byGroup.set(destination.group, [destination]);
    }
  }

  // WALKED IN THE DECLARATION'S ORDER rather than in the order groups were met, so the rail
  // reads the same whatever order the destinations happen to be written in.
  return DECLARED.filter((group) => byGroup.has(group.label)).map((group) => {
    const items = byGroup.get(group.label) ?? [];
    return { ...group, items, collapsed: group.icon !== undefined && items.length > 1 };
  });
}
